import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Card,
  CardBody,
  Badge,
  useColorModeValue,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Avatar,
  HStack,
  VStack,
  Progress,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Divider,
  Spinner,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Textarea,
  FormControl,
  FormLabel,
  ButtonGroup,
  Collapse
} from '@chakra-ui/react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faGraduationCap,
  faChartPie,
  faChartLine,
  faCheckCircle,
  faTimesCircle,
  faSearch,
  faDownload,
  faSync,
  faHistory,
  faArrowLeft,
  faClock,
  faTrophy,
  faAward,
  faFileCsv,
  faFileCode,
  faFilePdf,
  faFilter,
  faCopy,
  faBookOpen,
  faCheck,
  faTimes,
  faChevronDown,
  faChevronUp,
  faBrain,
  faQuestionCircle,
  faExclamationTriangle,
  faChevronRight,
  faChevronLeft,
  faGlobe,
  faLayerGroup,
  faEnvelope,
  faPaperPlane,
  faCalendarAlt,
  faEye,
  faPrint,
  faExpand,
  faCompress,
  faSlidersH,
  faChartBar,
  faArrowTrendUp
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useDynamoClient, useApp } from '@/hooks/contextHooks';
import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { apiGetRequest } from '@/functions/requests';
import { AppMeta } from '@xnav/types/src/common';
import {
  EnrolledStudent,
  StudentQuizAttempt,
  CourseAnalyticsSummary,
  QuestionDifficultyItem,
  fetchCourseEnrollments,
  fetchStudentQuizHistory,
  computeCourseSummary,
  exportToCSV,
  exportToJSON,
  exportToPDF,
  sendInstructorEmail
} from '@/functions/analyticsService';
import QuestionDifficultyDeck from '@/components/questionDifficultyDeck';

// Clean, High-Clarity 2D SVG Monthly Intake Bar Graph (Card Size)
const MonthlyIntakeChart: React.FC<{ data: { month: string; enrolled: number }[] }> = ({ data }) => {
  const chartHeight = 155;
  const chartWidth = 460;
  const padding = { top: 25, right: 20, bottom: 30, left: 35 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  if (!data || data.length === 0) {
    return (
      <Flex justify="center" align="center" h={`${chartHeight}px`} color="gray.400" fontSize="xs">
        No enrollment intake records available
      </Flex>
    );
  }

  const displayData = data;
  const maxEnrolled = Math.max(...displayData.map(d => d.enrolled), 1);
  const midEnrolled = Math.max(Math.round(maxEnrolled / 2), 1);
  const barWidth = Math.min(innerWidth / (displayData.length * 2.0), 38);

  return (
    <Box w="100%" overflowX="auto">
      <svg width="100%" height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id="intakeBarGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="100%" stopColor="#059669" />
          </linearGradient>
        </defs>

        {/* Horizontal Grid lines */}
        <line x1={padding.left} y1={padding.top} x2={chartWidth - padding.right} y2={padding.top} stroke="rgba(148, 163, 184, 0.15)" strokeDasharray="3 3" />
        <text x={padding.left - 6} y={padding.top + 3} fill="#94a3b8" fontSize="9" fontWeight="600" textAnchor="end">
          {maxEnrolled}
        </text>

        <line x1={padding.left} y1={padding.top + innerHeight / 2} x2={chartWidth - padding.right} y2={padding.top + innerHeight / 2} stroke="rgba(148, 163, 184, 0.12)" strokeDasharray="3 3" />
        <text x={padding.left - 6} y={padding.top + innerHeight / 2 + 3} fill="#94a3b8" fontSize="9" fontWeight="600" textAnchor="end">
          {midEnrolled}
        </text>

        {/* Base X Axis line */}
        <line x1={padding.left} y1={padding.top + innerHeight} x2={chartWidth - padding.right} y2={padding.top + innerHeight} stroke="rgba(148, 163, 184, 0.25)" strokeWidth="1.5" />
        <text x={padding.left - 6} y={padding.top + innerHeight + 3} fill="#94a3b8" fontSize="9" fontWeight="600" textAnchor="end">
          0
        </text>

        {/* 2D Clean Gradient Bars */}
        {displayData.map((d, i) => {
          const barHeight = Math.max((d.enrolled / maxEnrolled) * innerHeight, 4);
          const x = padding.left + (i + 0.5) * (innerWidth / displayData.length) - barWidth / 2;
          const y = padding.top + innerHeight - barHeight;

          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                rx={4}
                fill="url(#intakeBarGrad)"
                style={{ transition: 'fill 0.2s ease, height 0.3s ease' }}
              />

              {/* Value on top of bar */}
              <text
                x={x + barWidth / 2}
                y={y - 6}
                fill="#38bdf8"
                fontSize="11"
                fontWeight="800"
                textAnchor="middle"
              >
                +{d.enrolled}
              </text>

              {/* Month label on X Axis */}
              <text
                x={x + barWidth / 2}
                y={chartHeight - 8}
                fill="#94a3b8"
                fontSize="10"
                fontWeight="600"
                textAnchor="middle"
              >
                {d.month}
              </text>
              <title>{`${d.month}: ${d.enrolled} students enrolled`}</title>
            </g>
          );
        })}
      </svg>
    </Box>
  );
};

// High-Resolution 2D SVG Graph for Enlarged Modal View (Widescreen 860x220)
const Enlarged2DMonthlyIntakeChart: React.FC<{ data: { month: string; enrolled: number }[] }> = ({ data }) => {
  const chartHeight = 220;
  const chartWidth = 860;
  const padding = { top: 35, right: 30, bottom: 35, left: 45 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  if (!data || data.length === 0) {
    return (
      <Flex justify="center" align="center" h={`${chartHeight}px`} color="gray.400" fontSize="sm">
        No enrollment intake records for this timeframe
      </Flex>
    );
  }

  const displayData = data;
  const maxEnrolled = Math.max(...displayData.map(d => d.enrolled), 1);
  const step = Math.max(Math.ceil(maxEnrolled / 4), 1);
  const gridTicks = [0, step, step * 2, step * 3, Math.max(step * 4, maxEnrolled)];
  const actualMax = gridTicks[gridTicks.length - 1];

  const barWidth = Math.min(innerWidth / (displayData.length * 2.8), 54);

  return (
    <Box w="100%" overflowX="auto">
      <svg width="100%" height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="intakeEnlargedGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="100%" stopColor="#059669" />
          </linearGradient>
        </defs>

        {/* Grid Lines and Y-Axis Scale */}
        {gridTicks.map((tickVal) => {
          const y = padding.top + innerHeight - (tickVal / actualMax) * innerHeight;
          return (
            <g key={tickVal}>
              <line
                x1={padding.left}
                y1={y}
                x2={chartWidth - padding.right}
                y2={y}
                stroke="rgba(148, 163, 184, 0.15)"
                strokeDasharray={tickVal === 0 ? 'none' : '4 4'}
                strokeWidth={tickVal === 0 ? 1.5 : 1}
              />
              <text
                x={padding.left - 10}
                y={y + 4}
                fill="#94a3b8"
                fontSize="11"
                fontWeight="700"
                textAnchor="end"
              >
                {tickVal}
              </text>
            </g>
          );
        })}

        {/* 2D Bars */}
        {displayData.map((d, i) => {
          const barHeight = Math.max((d.enrolled / actualMax) * innerHeight, 6);
          const x = padding.left + (i + 0.5) * (innerWidth / displayData.length) - barWidth / 2;
          const y = padding.top + innerHeight - barHeight;

          return (
            <g key={i}>
              {/* Background track column */}
              <rect
                x={x}
                y={padding.top}
                width={barWidth}
                height={innerHeight}
                rx={4}
                fill="rgba(148, 163, 184, 0.05)"
              />

              {/* 2D Bar */}
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                rx={4}
                fill="url(#intakeEnlargedGrad)"
                style={{ transition: 'all 0.3s ease' }}
              />

              {/* Value Pill on top */}
              <rect
                x={x + barWidth / 2 - 18}
                y={y - 24}
                width={36}
                height={18}
                rx={9}
                fill="rgba(14, 165, 233, 0.15)"
                stroke="rgba(56, 189, 248, 0.5)"
                strokeWidth="1"
              />
              <text
                x={x + barWidth / 2}
                y={y - 12}
                fill="#38bdf8"
                fontSize="11"
                fontWeight="800"
                textAnchor="middle"
              >
                +{d.enrolled}
              </text>

              {/* Month Label */}
              <text
                x={x + barWidth / 2}
                y={chartHeight - 10}
                fill="#94a3b8"
                fontSize="12"
                fontWeight="700"
                textAnchor="middle"
              >
                {d.month}
              </text>
            </g>
          );
        })}
      </svg>
    </Box>
  );
};

// Custom SVG Donut Chart for Score Mastery Breakdown
const DonutChart: React.FC<{ data: { label: string; count: number; percentage: number; color: string }[]; total: number; size?: number }> = ({ data, total, size = 160 }) => {
  const strokeWidth = size > 200 ? 32 : 24;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  let accumulatedPercent = 0;

  return (
    <Box position="relative" width={`${size}px`} height={`${size}px`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="transparent"
          stroke="rgba(148, 163, 184, 0.15)"
          strokeWidth={strokeWidth}
        />
        {data.map((item, idx) => {
          if (item.count === 0) return null;
          const strokeDasharray = `${(item.percentage / 100) * circumference} ${circumference}`;
          const strokeDashoffset = -((accumulatedPercent / 100) * circumference);
          accumulatedPercent += item.percentage;

          return (
            <circle
              key={idx}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="transparent"
              stroke={item.color}
              strokeWidth={strokeWidth}
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              style={{ transition: 'all 0.5s ease-in-out' }}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          );
        })}
      </svg>
      <Flex
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        align="center"
        justify="center"
        flexDir="column"
        pointerEvents="none"
      >
        <Text fontSize={size > 200 ? '3xl' : 'xl'} fontWeight="900" lineHeight="1">
          {total}
        </Text>
        <Text fontSize={size > 200 ? 'xs' : '2xs'} color="gray.500" fontWeight="600" textTransform="uppercase" mt={0.5}>
          Attempts
        </Text>
      </Flex>
    </Box>
  );
};

// Custom SVG Linear Timeline & Trend Chart (Matching the Premium Modal Curve)
const LinearTrendChart: React.FC<{
  data: { date: string; attempts: number; avgScore: number; passRate?: number }[];
  isEnlarged?: boolean;
  metric?: 'attempts' | 'score' | 'passRate';
}> = ({ data, isEnlarged = false, metric = 'attempts' }) => {
  const chartHeight = isEnlarged ? 230 : 150;
  const chartWidth = isEnlarged ? 860 : 460;
  const padding = isEnlarged ? { top: 35, right: 30, bottom: 35, left: 45 } : { top: 25, right: 25, bottom: 30, left: 35 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  if (!data || data.length === 0) {
    return (
      <Flex justify="center" align="center" h={`${chartHeight}px`} color="gray.400" fontSize="xs">
        No timeline activity available
      </Flex>
    );
  }

  const maxVal = metric === 'attempts' ? Math.max(...data.map(d => d.attempts), 5) : 100;

  const points = data.map((d, i) => {
    const val = metric === 'score' ? d.avgScore : metric === 'passRate' ? (d.passRate ?? 0) : d.attempts;
    const x = padding.left + (i / Math.max(data.length - 1, 1)) * innerWidth;
    const y = padding.top + innerHeight - (val / Math.max(maxVal, 1)) * innerHeight;
    return { x, y, val, ...d };
  });

  const pathD = points.reduce((acc, p, i) => {
    if (i === 0) return `M ${p.x} ${p.y}`;
    const prev = points[i - 1];
    const cx1 = prev.x + (p.x - prev.x) / 2;
    const cy1 = prev.y;
    const cx2 = prev.x + (p.x - prev.x) / 2;
    const cy2 = p.y;
    return `${acc} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${p.x} ${p.y}`;
  }, '');

  const areaD = points.length > 0
    ? `${pathD} L ${points[points.length - 1].x} ${padding.top + innerHeight} L ${points[0].x} ${padding.top + innerHeight} Z`
    : '';

  const gradId = isEnlarged ? `trendGradBig_${metric}` : `trendGrad_${metric}`;

  return (
    <Box w="100%" overflowX="auto">
      <svg width="100%" height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.38" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0.0" />
          </linearGradient>
          <linearGradient id={`${gradId}_stroke`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#0ea5e9" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        <line x1={padding.left} y1={padding.top} x2={chartWidth - padding.right} y2={padding.top} stroke="rgba(148, 163, 184, 0.12)" strokeDasharray="3 3" />
        <line x1={padding.left} y1={padding.top + innerHeight / 2} x2={chartWidth - padding.right} y2={padding.top + innerHeight / 2} stroke="rgba(148, 163, 184, 0.12)" strokeDasharray="3 3" />
        <line x1={padding.left} y1={padding.top + innerHeight} x2={chartWidth - padding.right} y2={padding.top + innerHeight} stroke="rgba(148, 163, 184, 0.25)" />

        {/* Area fill */}
        {areaD && <path d={areaD} fill={`url(#${gradId})`} />}

        {/* Spline Line */}
        {pathD && <path d={pathD} fill="none" stroke={`url(#${gradId}_stroke)`} strokeWidth={isEnlarged ? "3.5" : "2.8"} strokeLinecap="round" strokeLinejoin="round" />}

        {/* Glowing Dots & Labels */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={isEnlarged ? 6 : 4.5} fill="#0ea5e9" stroke="#ffffff" strokeWidth={isEnlarged ? 2.5 : 2} />
            <text x={p.x} y={chartHeight - 8} fill="#94a3b8" fontSize={isEnlarged ? "11" : "9"} textAnchor="middle" fontWeight="600">
              {p.date.length > 5 ? p.date.slice(5) : p.date}
            </text>
            <title>{`${p.date}: ${metric === 'score' ? `Avg Score: ${p.avgScore}%` : metric === 'passRate' ? `Pass Rate: ${p.passRate}%` : `${p.attempts} attempts`}`}</title>
          </g>
        ))}
      </svg>
    </Box>
  );
};

export default function CourseAnalytics() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams<{ courseId?: string }>();
  const currentAppContext = useApp() as AppMeta;
  const { user } = useAuthenticator();
  const dynamo = useDynamoClient() as DynamoDBClient;
  const toast = useToast();

  // Scroll ref for horizontal charts navigation
  const chartsScrollRef = useRef<HTMLDivElement>(null);

  // Role & Org
  const [userRole, setUserRole] = useState<string>('Instructor');
  const [userOrg, setUserOrg] = useState<string>('7DImaging');
  const [allCourses, setAllCourses] = useState<AppMeta[]>([]);

  // Selected Course or 'global'
  const initialCourseId = params.courseId || searchParams.get('courseId') || (currentAppContext?.id ? currentAppContext.id : 'global');
  const [selectedCourseId, setSelectedCourseId] = useState<string>(initialCourseId);
  const [courseSections, setCourseSections] = useState<any[]>([]);

  // Analytics Data
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [students, setStudents] = useState<EnrolledStudent[]>([]);
  const [attempts, setAttempts] = useState<StudentQuizAttempt[]>([]);
  const [summary, setSummary] = useState<CourseAnalyticsSummary | null>(null);

  // Table Filters & Search
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [sortBy, setSortBy] = useState<string>('lastActive');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [pageSize, setPageSize] = useState<number>(15);
  const [page, setPage] = useState<number>(1);
  const [studentTableQuizFilter, setStudentTableQuizFilter] = useState<string>('all');

  // Student Quiz History Modal
  const { isOpen: isHistoryOpen, onOpen: onHistoryOpen, onClose: onHistoryClose } = useDisclosure();
  const [selectedStudent, setSelectedStudent] = useState<EnrolledStudent | null>(null);
  const [studentHistory, setStudentHistory] = useState<StudentQuizAttempt[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [historyQuestionFilter, setHistoryQuestionFilter] = useState<'all' | number>('all');
  const [historyQuizFilter, setHistoryQuizFilter] = useState<string>('all');
  const [historyActiveTab, setHistoryActiveTab] = useState<number>(0);
  const [expandedAttemptId, setExpandedAttemptId] = useState<string | null>(null);

  // Extract all unique quizzes the student has attempted
  const studentAttemptedQuizzes = useMemo(() => {
    const map: Record<string, { quizId: string; title: string; count: number }> = {};
    studentHistory.forEach(att => {
      const qId = att.quizId || `${att.courseId}-quiz`;
      if (!map[qId]) {
        map[qId] = {
          quizId: qId,
          title: att.quizTitle || (att.chapterName ? `${att.chapterName} Quiz` : 'Course Assessment Quiz'),
          count: 0
        };
      }
      map[qId].count += 1;
    });
    return Object.values(map);
  }, [studentHistory]);

  // Extract all unique quiz question lengths from filtered student's history
  const availableQuestionCounts = useMemo(() => {
    const set = new Set<number>();
    const list = historyQuizFilter === 'all'
      ? studentHistory
      : studentHistory.filter(att =>
          att.quizId === historyQuizFilter ||
          (historyQuizFilter.endsWith('-quiz') && (!att.quizId || att.quizId === historyQuizFilter))
        );
    list.forEach(att => {
      if (att.totalQuestions && att.totalQuestions > 0) {
        set.add(att.totalQuestions);
      }
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [studentHistory, historyQuizFilter]);

  // Filtered attempts based on selected quiz and question count
  const filteredStudentHistory = useMemo(() => {
    let list = studentHistory;
    if (historyQuizFilter !== 'all') {
      list = list.filter(att =>
        att.quizId === historyQuizFilter ||
        (historyQuizFilter.endsWith('-quiz') && (!att.quizId || att.quizId === historyQuizFilter))
      );
    }
    if (historyQuestionFilter !== 'all') {
      list = list.filter(att => att.totalQuestions === historyQuestionFilter);
    }
    return list;
  }, [studentHistory, historyQuizFilter, historyQuestionFilter]);

  // Chronological timeline data points for Recharts AreaChart
  const studentChartData = useMemo(() => {
    const sorted = [...filteredStudentHistory].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
    return sorted.map((att, idx) => {
      const dateObj = att.dateString ? new Date(att.dateString) : att.timestamp ? new Date(att.timestamp) : new Date();
      const formattedDate = dateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      const formattedTime = dateObj.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
      const pct = att.percentage !== undefined && att.percentage !== null
        ? att.percentage
        : Math.round(((att.score || 0) / (att.totalQuestions || 1)) * 100);

      return {
        attemptIndex: idx + 1,
        label: `Attempt ${idx + 1}`,
        shortLabel: `#${idx + 1} (${att.totalQuestions || 20}Q)`,
        percentage: pct,
        score: att.score || 0,
        totalQuestions: att.totalQuestions || 20,
        passed: att.passed !== undefined ? att.passed : pct >= 70,
        quizTitle: att.quizTitle || 'Course Assessment Quiz',
        chapterName: att.chapterName || 'General Examination',
        date: formattedDate,
        time: formattedTime,
        fullDate: `${formattedDate}, ${formattedTime}`,
        timeSpent: att.timeSpentSeconds ? `${Math.floor(att.timeSpentSeconds / 60)}m ${att.timeSpentSeconds % 60}s` : '2m 0s',
      };
    });
  }, [filteredStudentHistory]);

  // Performance breakdown categorized by quiz length (5 questions vs 10 vs 20)
  const studentQuestionBreakdown = useMemo(() => {
    const map: Record<number, { count: number; totalScore: number; totalQuestions: number; passedCount: number }> = {};
    filteredStudentHistory.forEach(att => {
      const qCount = att.totalQuestions || 20;
      if (!map[qCount]) {
        map[qCount] = { count: 0, totalScore: 0, totalQuestions: 0, passedCount: 0 };
      }
      map[qCount].count += 1;
      map[qCount].totalScore += (att.score || 0);
      map[qCount].totalQuestions += qCount;
      if (att.passed || att.percentage >= 70) {
        map[qCount].passedCount += 1;
      }
    });
    return Object.entries(map).map(([qCount, data]) => ({
      questionsPerQuiz: Number(qCount),
      attempts: data.count,
      totalQuestionsAnswered: data.totalQuestions,
      correctAnswers: data.totalScore,
      avgPercentage: data.totalQuestions > 0 ? Math.round((data.totalScore / data.totalQuestions) * 100) : 0,
      passRate: data.count > 0 ? Math.round((data.passedCount / data.count) * 100) : 0,
    })).sort((a, b) => a.questionsPerQuiz - b.questionsPerQuiz);
  }, [filteredStudentHistory]);

  // Comprehensive aggregate stats across filtered drilldown attempts
  const studentAggregateStats = useMemo(() => {
    const listToCompute = filteredStudentHistory;
    const totalQuestions = listToCompute.reduce((sum, att) => sum + (att.totalQuestions || 0), 0);
    const totalCorrect = listToCompute.reduce((sum, att) => sum + (att.score || 0), 0);
    const avgPct = listToCompute.length > 0
      ? Math.round(listToCompute.reduce((sum, att) => sum + (att.percentage || 0), 0) / listToCompute.length)
      : (selectedStudent?.avgScorePercentage || 0);
    const highestPct = listToCompute.length > 0
      ? Math.max(...listToCompute.map(att => att.percentage || 0))
      : (selectedStudent?.highestScore || 0);
    const overallAccuracy = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;
    const passedAttempts = listToCompute.filter(att => att.passed || att.percentage >= 70).length;
    const passRate = listToCompute.length > 0 ? Math.round((passedAttempts / listToCompute.length) * 100) : 0;

    return {
      totalAttempts: listToCompute.length,
      avgScore: avgPct,
      highestScore: highestPct,
      totalQuestions,
      totalCorrect,
      overallAccuracy,
      passRate,
    };
  }, [filteredStudentHistory, selectedStudent]);

  // Email Student Modal
  const { isOpen: isEmailOpen, onOpen: onEmailOpen, onClose: onEmailClose } = useDisclosure();
  const [emailTargetStudent, setEmailTargetStudent] = useState<EnrolledStudent | null>(null);
  const [emailSubject, setEmailSubject] = useState<string>('');
  const [emailBody, setEmailBody] = useState<string>('');
  const [instructorSenderName, setInstructorSenderName] = useState<string>('Dr. Cliff Gronseth');
  const [sendingEmail, setSendingEmail] = useState<boolean>(false);

  // PDF Preview Modal
  const { isOpen: isPdfPreviewOpen, onOpen: onPdfPreviewOpen, onClose: onPdfPreviewClose } = useDisclosure();
  const [pdfWithGraphs, setPdfWithGraphs] = useState<boolean>(true);

  // Enlarged Chart Modal
  const { isOpen: isEnlargeModalOpen, onOpen: onEnlargeModalOpen, onClose: onEnlargeModalClose } = useDisclosure();
  const [enlargedChartType, setEnlargedChartType] = useState<'mastery' | 'intake' | 'chapters' | 'activity' | 'comparison'>('intake');
  const [enlargedDateFilter, setEnlargedDateFilter] = useState<'all' | '7d' | '14d' | '30d' | '90d'>('all');
  const [enlargedMetric, setEnlargedMetric] = useState<'attempts' | 'score' | 'passRate'>('attempts');
  const [enlargedSort, setEnlargedSort] = useState<'asc' | 'desc'>('desc');

  // Currently logged in user email dynamically determined from Cognito auth session
  const currentLoggedInEmail = useMemo(() => {
    return (user?.signInDetails?.loginId || user?.username || 'cliff.gronseth@7dimaging.com').toLowerCase().trim();
  }, [user]);

  // Check if student matches currently logged in session user
  const isCurrentLoggedInUser = (studentEmail?: string) => {
    if (!studentEmail) return false;
    return studentEmail.toLowerCase().trim() === currentLoggedInEmail;
  };

  // Resolve instructor sender name
  useEffect(() => {
    if (currentLoggedInEmail.includes('cliff') || currentLoggedInEmail.includes('gronseth')) {
      setInstructorSenderName('Dr. Cliff Gronseth');
    } else if (currentLoggedInEmail.includes('bsk') || currentLoggedInEmail.includes('reddy')) {
      setInstructorSenderName('Boreddy');
    } else {
      setInstructorSenderName(userOrg || 'XNAV Instructor');
    }
  }, [currentLoggedInEmail, userOrg]);

  // 1. Discover all available quizzes for the course scope
  const availableQuizzes = useMemo(() => {
    const map: Record<string, {
      quizId: string;
      title: string;
      chapterName: string;
      isSectionQuiz: boolean;
      attemptCount: number;
    }> = {};

    function traverse(sections: any[], parentTitle = '') {
      sections.forEach(s => {
        const currentChapter = parentTitle ? `${parentTitle} › ${s.title}` : s.title;
        if (s.contents && Array.isArray(s.contents)) {
          s.contents.forEach((item: any) => {
            if (item.type === 'quiz') {
              map[item.id] = {
                quizId: item.id,
                title: item.title || 'Section Quiz',
                chapterName: currentChapter,
                isSectionQuiz: true,
                attemptCount: 0
              };
            }
          });
          const subSections = s.contents.filter((item: any) => item.type === 'section');
          if (subSections.length > 0) {
            traverse(subSections, currentChapter);
          }
        }
      });
    }

    if (courseSections && courseSections.length > 0) {
      traverse(courseSections);
    }

    attempts.forEach(a => {
      const qId = a.quizId || `${a.courseId}-quiz`;
      const isSection = a.chapterName &&
        a.chapterName !== 'General' &&
        a.chapterName !== 'General Examination' &&
        a.chapterName !== 'General Assessment' &&
        !qId.endsWith('-quiz');

      if (!map[qId]) {
        map[qId] = {
          quizId: qId,
          title: a.quizTitle || (isSection ? `${a.chapterName} Quiz` : 'Course Assessment Quiz'),
          chapterName: a.chapterName || (isSection ? a.chapterName : 'General Assessment'),
          isSectionQuiz: Boolean(isSection),
          attemptCount: 0
        };
      }
      map[qId].attemptCount += 1;
    });

    return Object.values(map);
  }, [courseSections, attempts]);

  const courseQuizzes = useMemo(() => availableQuizzes.filter(q => !q.isSectionQuiz), [availableQuizzes]);
  const sectionQuizzes = useMemo(() => availableQuizzes.filter(q => q.isSectionQuiz), [availableQuizzes]);

  // Selected Quiz Scope Label for the Enrolled Students Table
  const selectedStudentQuizLabel = useMemo(() => {
    if (studentTableQuizFilter === 'all') {
      return 'All Quizzes & Assessments';
    }
    const found = availableQuizzes.find(q => q.quizId === studentTableQuizFilter);
    if (found) {
      return found.isSectionQuiz
        ? `[Section] ${found.title}`
        : `[Course] ${found.title}`;
    }
    return 'Selected Quiz';
  }, [studentTableQuizFilter, availableQuizzes]);

  // Dynamically calculate individual student stats for the selected quiz
  const studentsWithQuizMetrics = useMemo(() => {
    if (studentTableQuizFilter === 'all') {
      return students;
    }

    return students.map(s => {
      const studentQuizAttempts = attempts.filter(a => {
        if (a.userId !== s.userId) return false;
        if (a.quizId === studentTableQuizFilter) return true;
        // Fallback for course quiz
        if (studentTableQuizFilter.endsWith('-quiz') && (!a.quizId || a.quizId === studentTableQuizFilter)) return true;
        return false;
      });

      const count = studentQuizAttempts.length;
      if (count === 0) {
        return {
          ...s,
          quizAttemptsCount: 0,
          avgScorePercentage: 0,
          highestScore: 0,
          passRate: 0,
          lastAttemptTimestamp: undefined,
          status: 'Not Attempted' as any
        };
      }

      const sumScore = studentQuizAttempts.reduce((acc, a) => acc + (a.percentage || 0), 0);
      const avg = Math.round(sumScore / count);
      const high = Math.max(...studentQuizAttempts.map(a => a.percentage || 0));
      const passedCount = studentQuizAttempts.filter(a => a.passed).length;
      const rate = Math.round((passedCount / count) * 100);
      const sorted = [...studentQuizAttempts].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

      return {
        ...s,
        quizAttemptsCount: count,
        avgScorePercentage: avg,
        highestScore: high,
        passRate: rate,
        lastAttemptTimestamp: sorted[0]?.timestamp,
        status: (rate >= 70 ? 'Completed' : 'Active') as any
      };
    });
  }, [students, attempts, studentTableQuizFilter]);

  // Filtered & Sorted Students (Pinning Logged In User to Top)
  const filteredStudents = useMemo(() => {
    let list = [...studentsWithQuizMetrics];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        s =>
          (s.userName && s.userName.toLowerCase().includes(q)) ||
          (s.userEmail && s.userEmail.toLowerCase().includes(q)) ||
          (s.userId && s.userId.toLowerCase().includes(q)) ||
          (s.courseName && s.courseName.toLowerCase().includes(q))
      );
    }

    if (statusFilter !== 'All') {
      list = list.filter(s => s.status === statusFilter);
    }

    list.sort((a, b) => {
      // Pin current logged in account to the top
      const isUserA = isCurrentLoggedInUser(a.userEmail);
      const isUserB = isCurrentLoggedInUser(b.userEmail);
      if (isUserA && !isUserB) return -1;
      if (!isUserA && isUserB) return 1;

      let valA: any = a[sortBy as keyof EnrolledStudent] ?? '';
      let valB: any = b[sortBy as keyof EnrolledStudent] ?? '';

      if (sortBy === 'userName') {
        valA = (a.userName || '').toLowerCase();
        valB = (b.userName || '').toLowerCase();
      } else if (sortBy === 'lastAttempt' || sortBy === 'lastAttemptDate') {
        valA = a.lastAttemptTimestamp || 0;
        valB = b.lastAttemptTimestamp || 0;
      }

      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [studentsWithQuizMetrics, searchQuery, statusFilter, sortBy, sortOrder, currentLoggedInEmail]);

  // Paginated Students
  const paginatedStudents = useMemo(() => {
    if (pageSize === -1) return filteredStudents;
    const start = (page - 1) * pageSize;
    return filteredStudents.slice(start, start + pageSize);
  }, [filteredStudents, page, pageSize]);

  const totalPages = pageSize === -1 ? 1 : Math.ceil(filteredStudents.length / pageSize) || 1;

  // Colors
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const mutedText = useColorModeValue('gray.500', 'gray.400');

  // Horizontal Scroll Handler
  const handleScrollCharts = (direction: 'left' | 'right') => {
    if (chartsScrollRef.current) {
      const scrollAmount = 380;
      chartsScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Load User details and accessible courses
  useEffect(() => {
    const initUserData = async () => {
      if (user?.signInDetails?.loginId) {
        try {
          const uEmail = user.signInDetails.loginId;
          const uData = await apiGetRequest<undefined, { role: string; org?: string }>(`/users/${encodeURIComponent(uEmail)}`);
          if (uData?.role) setUserRole(uData.role);
          if (uData?.org) setUserOrg(uData.org);
        } catch (e) {
          console.log('Error loading user role:', e);
        }
      }

      try {
        const apps = await apiGetRequest<undefined, AppMeta[]>('/apps');
        if (Array.isArray(apps)) {
          setAllCourses(apps);
        }
      } catch (e) {
        console.log('Error loading courses list:', e);
      }
    };
    initUserData();
  }, [user]);

  // Course map for names
  const courseMap = useMemo(() => {
    const map: Record<string, string> = {};
    allCourses.forEach(c => {
      map[c.id] = c.name;
    });
    map['b29691bf-47a3-480f-84cd-10f510dfc1d1'] = 'MSK KNEE nav 1.0';
    map['e1eb0b2b-cae7-432a-b45d-6467ed47adb3'] = 'MPDSR nav';
    map['f6992f12-f719-4859-b949-0b6e62a610d3'] = 'NYAGI TRIAGE 1st Trim';
    map['c6607593-2a3a-4ef5-b63f-94f541dc209e'] = 'NYAGI CLINIC nav';
    map['ac49fe22-7a0d-41f8-9b84-1f968f7a8e07'] = 'NYAGI EMED nav';
    map['53890cf1-297a-42e1-80d4-c9b3974781b3'] = 'NYAGI TRIAGE 2-3 Trim';
    map['4ec10e2d-20b1-403d-9720-2fe87e55750f'] = 'Samsung V7 nav';
    map['86df46c4-989f-4517-b84d-26b539543e33'] = 'HYPERFINE MRI';
    map['b387cfaf-e2b3-453b-93db-107b6fc98f81'] = 'ESSENTIAL NEWBORN CARE';
    map['e8a0c872-bba7-4a4a-8e9a-5ad613edc479'] = 'SONEX CTR';
    return map;
  }, [allCourses]);

  // Load Analytics Data for Selected Course (or Global)
  const loadAnalytics = async (isManualRefresh = false) => {
    if (isManualRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const { students: loadedStudents, attempts: loadedAttempts } = await fetchCourseEnrollments(dynamo, selectedCourseId);
      
      const studentsWithNames = loadedStudents.map(s => ({
        ...s,
        courseName: courseMap[s.courseId] || (s.courseId === 'b29691bf-47a3-480f-84cd-10f510dfc1d1' ? 'MSK KNEE nav 1.0' : s.courseId.slice(0, 10))
      }));

      setStudents(studentsWithNames);
      setAttempts(loadedAttempts);
      const computed = computeCourseSummary(studentsWithNames, loadedAttempts, courseMap);
      setSummary(computed);
    } catch (e) {
      console.error('Error fetching course analytics:', e);
      toast({
        title: 'Error loading analytics',
        description: 'Could not retrieve enrollment or quiz attempts data from DynamoDB.',
        status: 'error',
        duration: 4000,
        isClosable: true
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, [selectedCourseId, dynamo, courseMap]);

  // Load course structure (for section quizzes)
  useEffect(() => {
    if (selectedCourseId && selectedCourseId !== 'global') {
      apiGetRequest<string, any[]>(`/structure/${selectedCourseId}`)
        .then(data => {
          if (Array.isArray(data)) {
            setCourseSections(data);
          } else {
            setCourseSections([]);
          }
        })
        .catch(() => setCourseSections([]));
    } else {
      setCourseSections([]);
    }
  }, [selectedCourseId]);

  const isGlobal = selectedCourseId === 'global';

  // Active Course Metadata
  const currentCourseMeta = useMemo(() => {
    if (isGlobal) {
      return {
        id: 'global',
        name: 'All Platform Courses',
        creator: 'Global Organization',
        org: userOrg || 'Global',
        status: 'Active'
      } as AppMeta;
    }
    const found = allCourses.find(c => c.id === selectedCourseId);
    if (found) return found;
    if (currentAppContext?.id === selectedCourseId) return currentAppContext;
    return {
      id: selectedCourseId,
      name: courseMap[selectedCourseId] || 'MSK KNEE nav 1.0',
      creator: 'cliff.gronseth',
      org: '7DImaging',
      status: 'Live'
    } as AppMeta;
  }, [allCourses, selectedCourseId, currentAppContext, isGlobal, courseMap, userOrg]);

  // Filtered and sorted data specifically for the Enlarged Modal view
  const enlargedModalData = useMemo(() => {
    const now = Date.now();
    const daysLimit =
      enlargedDateFilter === '7d' ? 7 :
      enlargedDateFilter === '14d' ? 14 :
      enlargedDateFilter === '30d' ? 30 :
      enlargedDateFilter === '90d' ? 90 :
      null;

    const cutoffTimestamp = daysLimit ? now - daysLimit * 86400 * 1000 : 0;

    // 1. Filtered Attempts
    const filteredAttempts = cutoffTimestamp > 0
      ? attempts.filter(a => (a.timestamp || 0) >= cutoffTimestamp)
      : [...attempts];

    // 2. Filtered Students (by downloadedAt)
    const filteredEnrolledStudents = cutoffTimestamp > 0
      ? students.filter(s => (s.downloadedAt || 0) >= cutoffTimestamp)
      : [...students];

    // 3. Compute KPI stats for this date filter
    const totalAtt = filteredAttempts.length;
    const passedAtt = filteredAttempts.filter(a => a.passed || (a.percentage || 0) >= 70).length;
    const avgScore = totalAtt > 0
      ? Math.round(filteredAttempts.reduce((sum, a) => sum + (a.percentage || 0), 0) / totalAtt)
      : 0;
    const overallPassRate = totalAtt > 0 ? Math.round((passedAtt / totalAtt) * 100) : 0;

    // 4. Score Mastery Breakdown
    const tiers = [
      { label: 'Mastery (90-100%)', count: 0, color: '#10b981' },
      { label: 'Proficient (80-89%)', count: 0, color: '#06b6d4' },
      { label: 'Passing (70-79%)', count: 0, color: '#3b82f6' },
      { label: 'Needs Practice (<70%)', count: 0, color: '#f59e0b' }
    ];
    filteredAttempts.forEach(a => {
      const p = a.percentage || 0;
      if (p >= 90) tiers[0].count++;
      else if (p >= 80) tiers[1].count++;
      else if (p >= 70) tiers[2].count++;
      else tiers[3].count++;
    });
    const masteryBreakdown = tiers.map(t => ({
      ...t,
      percentage: totalAtt > 0 ? Math.round((t.count / totalAtt) * 100) : 0
    }));

    // Score distribution buckets
    const buckets = [
      { range: '0-20%', min: 0, max: 20, count: 0, color: '#ef4444' },
      { range: '21-40%', min: 21, max: 40, count: 0, color: '#f97316' },
      { range: '41-60%', min: 41, max: 60, count: 0, color: '#eab308' },
      { range: '61-80%', min: 61, max: 80, count: 0, color: '#06b6d4' },
      { range: '81-100%', min: 81, max: 100, count: 0, color: '#10b981' }
    ];
    filteredAttempts.forEach(a => {
      const pct = a.percentage || 0;
      const b = buckets.find(bk => pct >= bk.min && pct <= bk.max);
      if (b) b.count++;
    });

    // 5. Monthly / Daily Intake based on date filter
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let intakeData: { month: string; enrolled: number; sortKey: number }[] = [];

    if (enlargedDateFilter === '7d' || enlargedDateFilter === '14d') {
      const dayMap: Record<string, { month: string; enrolled: number; sortKey: number }> = {};
      const numDays = enlargedDateFilter === '7d' ? 7 : 14;
      for (let i = numDays - 1; i >= 0; i--) {
        const d = new Date(now - i * 86400 * 1000);
        const dKey = `${monthNames[d.getMonth()]} ${d.getDate()}`;
        const sortKey = d.getTime();
        dayMap[dKey] = { month: dKey, enrolled: 0, sortKey };
      }
      filteredEnrolledStudents.forEach(s => {
        if (s.downloadedAt) {
          const d = new Date(s.downloadedAt);
          const dKey = `${monthNames[d.getMonth()]} ${d.getDate()}`;
          if (dayMap[dKey]) dayMap[dKey].enrolled++;
        }
      });
      intakeData = Object.values(dayMap);
    } else {
      const monthMap: Record<string, { month: string; enrolled: number; sortKey: number }> = {};
      filteredEnrolledStudents.forEach(s => {
        if (s.downloadedAt) {
          const d = new Date(s.downloadedAt);
          const mIdx = d.getMonth();
          const yr = d.getFullYear();
          const mKey = `${monthNames[mIdx]} ${yr}`;
          const sortKey = yr * 100 + mIdx;
          if (!monthMap[mKey]) monthMap[mKey] = { month: mKey, enrolled: 0, sortKey };
          monthMap[mKey].enrolled++;
        }
      });
      intakeData = Object.values(monthMap);
    }

    if (enlargedSort === 'asc') {
      intakeData.sort((a, b) => a.sortKey - b.sortKey);
    } else {
      intakeData.sort((a, b) => b.sortKey - a.sortKey);
    }

    // 6. Chapters / Quiz breakdown
    const quizMap: Record<string, { title: string; chapter: string; attempts: StudentQuizAttempt[] }> = {};
    filteredAttempts.forEach(a => {
      const key = a.quizId || 'general';
      if (!quizMap[key]) {
        quizMap[key] = {
          title: a.quizTitle || 'Course Quiz',
          chapter: a.chapterName || 'General Chapter',
          attempts: []
        };
      }
      quizMap[key].attempts.push(a);
    });

    let quizBreakdown = Object.entries(quizMap).map(([quizId, data]) => {
      const attCount = data.attempts.length;
      const pCount = data.attempts.filter(a => (a.percentage || 0) >= 70).length;
      const avg = attCount > 0 ? Math.round(data.attempts.reduce((sum, a) => sum + (a.percentage || 0), 0) / attCount) : 0;
      const rate = attCount > 0 ? Math.round((pCount / attCount) * 100) : 0;
      return {
        quizId,
        title: data.title,
        chapter: data.chapter,
        attempts: attCount,
        passRate: rate,
        avgScore: avg
      };
    });

    quizBreakdown.sort((a, b) => {
      const valA = enlargedMetric === 'score' ? a.avgScore : enlargedMetric === 'passRate' ? a.passRate : a.attempts;
      const valB = enlargedMetric === 'score' ? b.avgScore : enlargedMetric === 'passRate' ? b.passRate : b.attempts;
      return enlargedSort === 'asc' ? valA - valB : valB - valA;
    });

    // 7. Course Comparison (for Global View)
    const courseGroup: Record<string, { enrolled: number; attempts: StudentQuizAttempt[] }> = {};
    filteredEnrolledStudents.forEach(s => {
      if (!courseGroup[s.courseId]) courseGroup[s.courseId] = { enrolled: 0, attempts: [] };
      courseGroup[s.courseId].enrolled++;
    });
    filteredAttempts.forEach(a => {
      if (!courseGroup[a.courseId]) courseGroup[a.courseId] = { enrolled: 0, attempts: [] };
      courseGroup[a.courseId].attempts.push(a);
    });

    let courseComparison = Object.entries(courseGroup).map(([cid, data]) => {
      const attCount = data.attempts.length;
      const pCount = data.attempts.filter(a => (a.percentage || 0) >= 70).length;
      const avg = attCount > 0 ? Math.round(data.attempts.reduce((sum, a) => sum + (a.percentage || 0), 0) / attCount) : 0;
      const rate = attCount > 0 ? Math.round((pCount / attCount) * 100) : 0;
      const cName = courseMap?.[cid] || (cid === 'b29691bf-47a3-480f-84cd-10f510dfc1d1' ? 'MSK KNEE nav 1.0' : cid.slice(0, 10));
      return {
        courseId: cid,
        courseName: cName,
        enrolled: data.enrolled,
        attempts: attCount,
        avgScore: avg,
        passRate: rate
      };
    });

    courseComparison.sort((a, b) => {
      const valA = enlargedMetric === 'score' ? a.avgScore : enlargedMetric === 'passRate' ? a.passRate : a.attempts;
      const valB = enlargedMetric === 'score' ? b.avgScore : enlargedMetric === 'passRate' ? b.passRate : b.attempts;
      return enlargedSort === 'asc' ? valA - valB : valB - valA;
    });

    // 8. Activity Timeline
    const timelineMap: Record<string, { total: number; scoreSum: number; passSum: number }> = {};
    filteredAttempts.forEach(a => {
      const dateStr = a.dateString ? a.dateString.split('T')[0] : (a.timestamp ? new Date(a.timestamp).toISOString().split('T')[0] : 'Recent');
      if (!timelineMap[dateStr]) {
        timelineMap[dateStr] = { total: 0, scoreSum: 0, passSum: 0 };
      }
      timelineMap[dateStr].total++;
      timelineMap[dateStr].scoreSum += (a.percentage || 0);
      if ((a.percentage || 0) >= 70 || a.passed) timelineMap[dateStr].passSum++;
    });

    let activityTimeline = Object.entries(timelineMap).map(([date, d]) => ({
      date,
      attempts: d.total,
      avgScore: Math.round(d.scoreSum / d.total),
      passRate: Math.round((d.passSum / d.total) * 100)
    }));

    activityTimeline.sort((a, b) => {
      return enlargedSort === 'asc' ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date);
    });

    return {
      filteredAttempts,
      filteredEnrolledStudents,
      totalEnrolled: filteredEnrolledStudents.length,
      activeStudents: filteredEnrolledStudents.filter(s => s.status === 'Active').length,
      totalAttempts: totalAtt,
      averageScore: avgScore,
      overallPassRate,
      masteryBreakdown,
      scoreDistribution: buckets,
      monthlyIntake: intakeData,
      quizBreakdown,
      courseComparison,
      activityTimeline
    };
  }, [attempts, students, enlargedDateFilter, enlargedMetric, enlargedSort, courseMap]);

  // Open Student History Drilldown
  const handleViewStudentHistory = async (student: EnrolledStudent) => {
    setSelectedStudent(student);
    setStudentHistory([]);
    setHistoryQuestionFilter('all');
    setHistoryQuizFilter(studentTableQuizFilter);
    setHistoryActiveTab(studentTableQuizFilter !== 'all' ? 1 : 0);
    setHistoryLoading(true);
    onHistoryOpen();

    try {
      const history = await fetchStudentQuizHistory(dynamo, student.userId);
      setStudentHistory(history);
    } catch (e) {
      console.error('Failed to load student history:', e);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Open Enlarge Modal
  const handleOpenEnlargeModal = (type: 'mastery' | 'intake' | 'chapters' | 'activity' | 'comparison') => {
    setEnlargedChartType(type);
    onEnlargeModalOpen();
  };

  const handleOpenEmailModal = (student: EnrolledStudent) => {
    setEmailTargetStudent(student);
    setEmailSubject(`Progress Update: ${currentCourseMeta.name || 'Your Course'} & Quiz Performance`);
    setEmailBody('');
    onEmailOpen();
  };

  const handleSendEmailToStudent = async () => {
    if (!emailTargetStudent?.userEmail || !emailBody.trim()) {
      toast({ title: 'Error', description: 'Recipient and message body are required.', status: 'error', duration: 3000 });
      return;
    }
    setSendingEmail(true);
    try {
      const fullMessage = `${emailBody.trim()}\n\nBest regards,\n${instructorSenderName}\n${currentCourseMeta.org || userOrg || 'XNAV Studio'} Instructor Team`;
      
      const res = await sendInstructorEmail({
        recipientEmail: emailTargetStudent.userEmail,
        studentName: emailTargetStudent.userName || 'Student',
        courseName: currentCourseMeta.name || 'Course',
        subject: emailSubject,
        messageBody: fullMessage,
        instructorName: instructorSenderName
      });
      toast({
        title: 'Email Sent Successfully',
        description: `Notification delivered directly to ${emailTargetStudent.userEmail}`,
        status: 'success',
        duration: 4000,
        isClosable: true
      });
      onEmailClose();
    } catch (e: any) {
      toast({
        title: 'Failed to send email',
        description: e.message || 'Error occurred while sending email.',
        status: 'error',
        duration: 4000
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDownloadPDF = async (withGraphs: boolean) => {
    const chartsEl = withGraphs ? document.getElementById('analytics-charts-grid') : null;
    await exportToPDF(filteredStudents, summary, currentCourseMeta.name || 'Analytics', withGraphs, chartsEl);
    toast({
      title: withGraphs ? 'PDF Export (Full Report with Graphs) Complete' : 'PDF Export (Roster Only) Complete',
      description: `Generated executive PDF report for ${filteredStudents.length} students.`,
      status: 'success',
      duration: 3500,
      isClosable: true
    });
  };

  const handleExportCSV = () => {
    exportToCSV(filteredStudents, currentCourseMeta.name || 'Analytics');
    toast({
      title: 'Export CSV Complete',
      description: `Exported ${filteredStudents.length} student records from DynamoDB.`,
      status: 'success',
      duration: 3000,
      isClosable: true
    });
  };

  const handleExportJSON = () => {
    exportToJSON({ course: currentCourseMeta, summary, students: filteredStudents, attempts }, currentCourseMeta.name || 'Analytics');
    toast({
      title: 'Export JSON Complete',
      description: 'Analytics data payload exported successfully.',
      status: 'success',
      duration: 3000,
      isClosable: true
    });
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: `${label} Copied`,
      status: 'info',
      duration: 2000,
      isClosable: true
    });
  };

  return (
    <Box minH="100vh" bg={bg} p={{ base: 4, md: 8 }} transition="all 0.2s">
      {/* Top Header & Context Bar */}
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
        <Box>
          <HStack spacing={2} align="center">
            <Text fontSize="xs" fontWeight="700" color="emerald.500" textTransform="uppercase" letterSpacing="1px">
              {currentCourseMeta.org || userOrg}
            </Text>
            <Text fontSize="xs" color="gray.400">/</Text>
            <Text fontSize="xs" fontWeight="600" color={mutedText}>
              {isGlobal ? 'Global Organization Analytics' : 'Course Student Analytics'}
            </Text>
          </HStack>
          <HStack spacing={2} align="center" mt={0.5}>
            <Heading size="lg" fontWeight="800" letterSpacing="-0.5px">
              {currentCourseMeta.name}
            </Heading>
            {isGlobal && (
              <Badge bg="#0B192C" color="#10B981" border="1px solid rgba(16, 185, 129, 0.4)" borderRadius="full" px={2.5} py={0.5} fontSize="2xs" fontWeight="800" shadow="0 0 10px rgba(16, 185, 129, 0.2)">
                SUPER ADMIN
              </Badge>
            )}
          </HStack>
        </Box>

        <HStack spacing={3} flexWrap="wrap">
          {/* Global / Course Switcher */}
          <Select
            size="sm"
            w={{ base: '200px', md: '280px' }}
            bg={cardBg}
            borderColor={borderColor}
            borderRadius="lg"
            fontWeight="600"
            value={selectedCourseId}
            onChange={(e) => {
              setSelectedCourseId(e.target.value);
              setPage(1);
            }}
          >
            <option value="global">🌐 Global Overview (All Courses)</option>
            <option disabled>────────── Courses ──────────</option>
            {allCourses.length > 0 ? (
              allCourses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))
            ) : (
              <option value="b29691bf-47a3-480f-84cd-10f510dfc1d1">MSK KNEE nav 1.0</option>
            )}
          </Select>

          <Tooltip label="Refresh live data from DynamoDB">
            <IconButton
              aria-label="Refresh data"
              icon={<FontAwesomeIcon icon={faSync} spin={refreshing} />}
              size="sm"
              variant="outline"
              borderRadius="lg"
              onClick={() => loadAnalytics(true)}
            />
          </Tooltip>

          <Menu>
            <MenuButton as={Button} size="sm" colorScheme="green" leftIcon={<FontAwesomeIcon icon={faDownload} />} borderRadius="lg">
              Export
            </MenuButton>
            <MenuList bg={cardBg} borderColor={borderColor} shadow="2xl" borderRadius="xl" p={1.5}>
              <MenuItem icon={<FontAwesomeIcon icon={faFilePdf} color="#ef4444" />} onClick={() => handleDownloadPDF(true)}>
                Export PDF (Full Report with Graphs)
              </MenuItem>
              <MenuItem icon={<FontAwesomeIcon icon={faFilePdf} color="#f97316" />} onClick={() => handleDownloadPDF(false)}>
                Export PDF (Roster & Table Only)
              </MenuItem>
              <MenuItem icon={<FontAwesomeIcon icon={faEye} color="#3b82f6" />} onClick={onPdfPreviewOpen}>
                Preview Executive PDF Report
              </MenuItem>
              <Divider my={1} borderColor={borderColor} />
              <MenuItem icon={<FontAwesomeIcon icon={faFileCsv} color="#10b981" />} onClick={handleExportCSV}>
                Export to CSV
              </MenuItem>
              <MenuItem icon={<FontAwesomeIcon icon={faFileCode} color="#06b6d4" />} onClick={handleExportJSON}>
                Export to JSON
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" minH="50vh" flexDir="column" gap={4}>
          <Spinner size="xl" thickness="3px" color="#10b981" />
          <Text fontWeight="600" color={mutedText}>
            Loading course analytics and student performance records...
          </Text>
        </Flex>
      ) : (
        <VStack spacing={6} align="stretch">
          {/* KPI Metrics Cards */}
          <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4}>
            {/* Total Enrolled */}
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl" shadow="sm">
              <CardBody p={4}>
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="xs" fontWeight="700" color={mutedText} textTransform="uppercase">
                    Enrolled
                  </Text>
                  <Box p={2} bg="emerald.50" color="emerald.500" borderRadius="lg">
                    <FontAwesomeIcon icon={faUsers} />
                  </Box>
                </Flex>
                <Heading size="lg" fontWeight="800">
                  {summary?.totalEnrolled || students.length}
                </Heading>
                <Text fontSize="2xs" color="emerald.500" fontWeight="600" mt={1}>
                  Total Students
                </Text>
              </CardBody>
            </Card>

            {/* Active Learners */}
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl" shadow="sm">
              <CardBody p={4}>
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="xs" fontWeight="700" color={mutedText} textTransform="uppercase">
                    Active
                  </Text>
                  <Box p={2} bg="blue.50" color="blue.500" borderRadius="lg">
                    <FontAwesomeIcon icon={faGraduationCap} />
                  </Box>
                </Flex>
                <Heading size="lg" fontWeight="800">
                  {summary?.activeStudents || students.filter(s => s.status === 'Active').length}
                </Heading>
                <Text fontSize="2xs" color="blue.500" fontWeight="600" mt={1}>
                  Active Learners
                </Text>
              </CardBody>
            </Card>

            {/* Average Quiz Score */}
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl" shadow="sm">
              <CardBody p={4}>
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="xs" fontWeight="700" color={mutedText} textTransform="uppercase">
                    Avg Score
                  </Text>
                  <Box p={2} bg="teal.50" color="teal.500" borderRadius="lg">
                    <FontAwesomeIcon icon={faTrophy} />
                  </Box>
                </Flex>
                <Heading size="lg" fontWeight="800" color="#10b981">
                  {summary?.averageScore || 0}%
                </Heading>
                <Text fontSize="2xs" color="gray.500" fontWeight="600" mt={1}>
                  Average Performance
                </Text>
              </CardBody>
            </Card>

            {/* Overall Pass Rate */}
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl" shadow="sm">
              <CardBody p={4}>
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="xs" fontWeight="700" color={mutedText} textTransform="uppercase">
                    Pass Rate
                  </Text>
                  <Box p={2} bg="purple.50" color="purple.500" borderRadius="lg">
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </Box>
                </Flex>
                <Heading size="lg" fontWeight="800" color={summary && summary.overallPassRate >= 70 ? 'emerald.500' : 'amber.500'}>
                  {summary?.overallPassRate || 0}%
                </Heading>
                <Text fontSize="2xs" color="gray.500" fontWeight="600" mt={1}>
                  Benchmark &ge; 70%
                </Text>
              </CardBody>
            </Card>

            {/* Total Quiz Attempts */}
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl" shadow="sm">
              <CardBody p={4}>
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="xs" fontWeight="700" color={mutedText} textTransform="uppercase">
                    Attempts
                  </Text>
                  <Box p={2} bg={useColorModeValue('cyan.50', 'rgba(14, 165, 233, 0.15)')} color="cyan.400" borderRadius="lg">
                    <FontAwesomeIcon icon={faChartLine} />
                  </Box>
                </Flex>
                <Heading size="lg" fontWeight="800">
                  {summary?.totalAttempts || attempts.length}
                </Heading>
                <Text fontSize="2xs" color="cyan.400" fontWeight="600" mt={1}>
                  Total Submissions
                </Text>
              </CardBody>
            </Card>

            {/* Global Courses / Completion */}
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl" shadow="sm">
              <CardBody p={4}>
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="xs" fontWeight="700" color={mutedText} textTransform="uppercase">
                    {isGlobal ? 'Courses' : 'Completion'}
                  </Text>
                  <Box p={2} bg="cyan.50" color="cyan.500" borderRadius="lg">
                    <FontAwesomeIcon icon={isGlobal ? faLayerGroup : faAward} />
                  </Box>
                </Flex>
                <Heading size="lg" fontWeight="800">
                  {isGlobal ? (summary?.courseComparison?.length || Object.keys(courseMap).length) : `${students.length > 0 ? Math.round((students.filter(s => s.status === 'Completed').length / students.length) * 100) : 0}%`}
                </Heading>
                <Text fontSize="2xs" color="cyan.500" fontWeight="600" mt={1}>
                  {isGlobal ? 'Active Courses' : 'Course Mastery'}
                </Text>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Section Header with Horizontal Scroll Controls */}
          <Flex justify="space-between" align="center" pt={1}>
            <HStack spacing={2}>
              <Heading size="sm" fontWeight="800">
                Interactive Visual Analytics Deck
              </Heading>
              <Badge bg="#0B192C" color="#10B981" border="1px solid rgba(16, 185, 129, 0.3)" borderRadius="full" px={2} py={0.5} fontSize="3xs">
                Live Charts
              </Badge>
            </HStack>

            <HStack spacing={1.5}>
              <Tooltip label="Scroll Charts Left">
                <IconButton
                  aria-label="Scroll Left"
                  icon={<FontAwesomeIcon icon={faChevronLeft} />}
                  size="xs"
                  variant="outline"
                  borderRadius="full"
                  onClick={() => handleScrollCharts('left')}
                />
              </Tooltip>
              <Tooltip label="Scroll Charts Right">
                <IconButton
                  aria-label="Scroll Right"
                  icon={<FontAwesomeIcon icon={faChevronRight} />}
                  size="xs"
                  variant="outline"
                  borderRadius="full"
                  onClick={() => handleScrollCharts('right')}
                />
              </Tooltip>
            </HStack>
          </Flex>

          {/* Visual Charts & Analysis Grid (With ID for PDF Capture and Horizontal Scroll Support) */}
          <Box
            id="analytics-charts-grid"
            ref={chartsScrollRef}
            overflowX="auto"
            pb={3}
            css={{
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { height: '6px' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(16, 185, 129, 0.3)', borderRadius: '4px' }
            }}
          >
            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5} minW={{ base: '100%', xl: '1100px' }}>
              {/* 1. Score Distribution & Donut Mastery Chart */}
              <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl" shadow="sm" position="relative">
                <CardBody p={5}>
                  <Flex justify="space-between" align="center" mb={3}>
                    <HStack spacing={2}>
                      <Box p={2} bg="linear-gradient(135deg, rgba(14, 165, 233, 0.12) 0%, rgba(16, 185, 129, 0.12) 100%)" color="#0284c7" borderRadius="lg">
                        <FontAwesomeIcon icon={faChartPie} />
                      </Box>
                      <Heading size="xs" fontWeight="800">
                        Mastery Breakdown
                      </Heading>
                    </HStack>
                    <HStack spacing={1.5}>
                      <Badge colorScheme="green" variant="subtle" px={2} py={0.5} borderRadius="full" fontSize="3xs">
                        Mastery Tiers
                      </Badge>
                      <Tooltip label="Enlarge & Advanced Filter Tools">
                        <IconButton
                          aria-label="Enlarge Chart"
                          icon={<FontAwesomeIcon icon={faExpand} />}
                          size="xs"
                          variant="ghost"
                          colorScheme="green"
                          onClick={() => handleOpenEnlargeModal('mastery')}
                        />
                      </Tooltip>
                    </HStack>
                  </Flex>

                  {summary?.masteryBreakdown && (
                    <Flex align="center" justify="space-around" py={1} flexWrap="wrap" gap={3}>
                      <DonutChart data={summary.masteryBreakdown} total={summary.totalAttempts} />
                      <VStack align="stretch" spacing={1.5} flex={1} minW="130px">
                        {summary.masteryBreakdown.map((m, idx) => (
                          <Flex key={idx} justify="space-between" align="center" fontSize="2xs">
                            <HStack spacing={1.5}>
                              <Box w="8px" h="8px" borderRadius="full" bg={m.color} />
                              <Text fontWeight="600">{m.label.split(' ')[0]}</Text>
                            </HStack>
                            <Text fontWeight="700" color={m.color}>
                              {m.count} ({m.percentage}%)
                            </Text>
                          </Flex>
                        ))}
                      </VStack>
                    </Flex>
                  )}

                  <Divider my={3} borderColor={borderColor} />

                  <VStack spacing={2} align="stretch">
                    {summary?.scoreDistribution.map((b) => {
                      const totalAtt = summary.totalAttempts || 1;
                      const pct = Math.round((b.count / totalAtt) * 100);
                      return (
                        <Box key={b.range}>
                          <Flex justify="space-between" fontSize="3xs" fontWeight="600" mb={0.5}>
                            <Text>{b.range}</Text>
                            <Text color={mutedText}>
                              {b.count} attempts ({pct}%)
                            </Text>
                          </Flex>
                          <Progress
                            value={pct}
                            size="xs"
                            borderRadius="full"
                            colorScheme={b.range.includes('81') ? 'green' : b.range.includes('61') ? 'teal' : b.range.includes('41') ? 'yellow' : 'red'}
                          />
                        </Box>
                      );
                    })}
                  </VStack>
                </CardBody>
              </Card>

              {/* 2. Monthly Student Intake & Growth Chart */}
              <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl" shadow="sm" position="relative">
                <CardBody p={5}>
                  <Flex justify="space-between" align="center" mb={3}>
                    <HStack spacing={2}>
                      <Box p={2} bg="linear-gradient(135deg, rgba(14, 165, 233, 0.12) 0%, rgba(16, 185, 129, 0.12) 100%)" color="#0284c7" borderRadius="lg">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                      </Box>
                      <Heading size="xs" fontWeight="800">
                        Monthly Intake & Growth
                      </Heading>
                    </HStack>
                    <HStack spacing={1.5}>
                      <Badge colorScheme="teal" variant="subtle" px={2} py={0.5} borderRadius="full" fontSize="3xs">
                        Monthly Cohorts
                      </Badge>
                      <Tooltip label="Enlarge & Advanced Filter Tools">
                        <IconButton
                          aria-label="Enlarge Chart"
                          icon={<FontAwesomeIcon icon={faExpand} />}
                          size="xs"
                          variant="ghost"
                          colorScheme="teal"
                          onClick={() => handleOpenEnlargeModal('intake')}
                        />
                      </Tooltip>
                    </HStack>
                  </Flex>

                  <Box mt={1}>
                    <MonthlyIntakeChart data={summary?.monthlyIntake || []} />
                    <Flex justify="space-between" align="center" mt={3} pt={2} borderTop="1px solid" borderColor={borderColor} fontSize="3xs" color={mutedText}>
                      <Text>Cumulative Enrollments</Text>
                      <Text fontWeight="bold" color="#10b981">
                        {students.length} Total Registered
                      </Text>
                    </Flex>
                  </Box>
                </CardBody>
              </Card>

              {/* 3. Course Comparison (if Global) or Chapter Performance */}
              {isGlobal ? (
                <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl" shadow="sm" position="relative">
                  <CardBody p={5}>
                    <Flex justify="space-between" align="center" mb={3}>
                      <HStack spacing={2}>
                        <Box p={2} bg="rgba(6, 182, 212, 0.1)" color="#06b6d4" borderRadius="lg">
                          <FontAwesomeIcon icon={faGlobe} />
                        </Box>
                        <Heading size="xs" fontWeight="800">
                          Course Comparison
                        </Heading>
                      </HStack>
                      <HStack spacing={1.5}>
                        <Badge colorScheme="cyan" variant="subtle" px={2} py={0.5} borderRadius="full" fontSize="3xs">
                          All Courses
                        </Badge>
                        <Tooltip label="Enlarge & Advanced Filter Tools">
                          <IconButton
                            aria-label="Enlarge Chart"
                            icon={<FontAwesomeIcon icon={faExpand} />}
                            size="xs"
                            variant="ghost"
                            colorScheme="cyan"
                            onClick={() => handleOpenEnlargeModal('comparison')}
                          />
                        </Tooltip>
                      </HStack>
                    </Flex>

                    <VStack spacing={2.5} align="stretch" maxH="280px" overflowY="auto" pr={1}>
                      {summary?.courseComparison && summary.courseComparison.length > 0 ? (
                        summary.courseComparison.map((c) => (
                          <Box
                            key={c.courseId}
                            p={2.5}
                            borderRadius="xl"
                            bg={useColorModeValue('gray.50', 'whiteAlpha.50')}
                            border="1px solid"
                            borderColor={borderColor}
                            cursor="pointer"
                            _hover={{ borderColor: '#10b981' }}
                            transition="all 0.15s"
                            onClick={() => setSelectedCourseId(c.courseId)}
                          >
                            <Flex justify="space-between" align="center" mb={1}>
                              <Box>
                                <Text fontSize="2xs" fontWeight="700" isTruncated maxW="150px">
                                  {c.courseName}
                                </Text>
                                <Text fontSize="3xs" color={mutedText}>
                                  {c.enrolled} stds &bull; {c.attempts} att
                                </Text>
                              </Box>
                              <HStack spacing={1.5}>
                                <Badge colorScheme={c.passRate >= 70 ? 'green' : 'orange'} fontSize="3xs" px={1.5} borderRadius="md">
                                  {c.passRate}%
                                </Badge>
                                <Text fontSize="2xs" fontWeight="bold" color="#10b981">
                                  {c.avgScore}%
                                </Text>
                              </HStack>
                            </Flex>
                            <Progress value={c.passRate} size="xs" borderRadius="full" colorScheme="green" />
                          </Box>
                        ))
                      ) : (
                        <Flex justify="center" align="center" py={8} color={mutedText}>
                          <Text fontSize="xs">No courses recorded yet.</Text>
                        </Flex>
                      )}
                    </VStack>
                  </CardBody>
                </Card>
              ) : (
                <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl" shadow="sm" position="relative">
                  <CardBody p={5}>
                    <Flex justify="space-between" align="center" mb={3}>
                      <HStack spacing={2}>
                        <Box p={2} bg="rgba(6, 182, 212, 0.1)" color="#06b6d4" borderRadius="lg">
                          <FontAwesomeIcon icon={faBookOpen} />
                        </Box>
                        <Heading size="xs" fontWeight="800">
                          Quiz & Chapter Pass Rates
                        </Heading>
                      </HStack>
                      <HStack spacing={1.5}>
                        <Badge colorScheme="cyan" variant="subtle" px={2} py={0.5} borderRadius="full" fontSize="3xs">
                          Chapters
                        </Badge>
                        <Tooltip label="Enlarge & Advanced Filter Tools">
                          <IconButton
                            aria-label="Enlarge Chart"
                            icon={<FontAwesomeIcon icon={faExpand} />}
                            size="xs"
                            variant="ghost"
                            colorScheme="cyan"
                            onClick={() => handleOpenEnlargeModal('chapters')}
                          />
                        </Tooltip>
                      </HStack>
                    </Flex>

                    <VStack spacing={2.5} align="stretch" maxH="280px" overflowY="auto" pr={1}>
                      {summary?.quizBreakdown && summary.quizBreakdown.length > 0 ? (
                        summary.quizBreakdown.map((q) => {
                          const isSection = q.chapter &&
                            q.chapter !== 'General' &&
                            q.chapter !== 'General Examination' &&
                            q.chapter !== 'General Assessment' &&
                            !q.quizId.endsWith('-quiz');

                          return (
                            <Box key={q.quizId} p={2.5} borderRadius="xl" bg={useColorModeValue('gray.50', 'whiteAlpha.50')} border="1px solid" borderColor={borderColor}>
                              <Flex justify="space-between" align="center" mb={1}>
                                <Box overflow="hidden" mr={2}>
                                  <HStack spacing={1.5} mb={0.5}>
                                    <Badge colorScheme={isSection ? 'purple' : 'cyan'} variant="subtle" fontSize="3xs" px={1.5} borderRadius="md">
                                      {isSection ? 'Section Quiz' : 'Course Quiz'}
                                    </Badge>
                                    <Text fontSize="2xs" fontWeight="700" isTruncated maxW="150px">
                                      {q.title}
                                    </Text>
                                  </HStack>
                                  <Text fontSize="3xs" color={mutedText}>
                                    {q.chapter} &bull; {q.attempts} att
                                  </Text>
                                </Box>
                                <HStack spacing={1.5} flexShrink={0}>
                                  <Badge colorScheme={q.passRate >= 70 ? 'green' : 'orange'} fontSize="3xs" px={1.5} borderRadius="md">
                                    {q.passRate}%
                                  </Badge>
                                  <Text fontSize="2xs" fontWeight="bold" color="#10b981">
                                    {q.avgScore}%
                                  </Text>
                                </HStack>
                              </Flex>
                              <Progress value={q.passRate} size="xs" borderRadius="full" colorScheme="green" />
                            </Box>
                          );
                        })
                      ) : (
                        <Flex justify="center" align="center" py={8} color={mutedText}>
                          <Text fontSize="xs">No chapter attempts recorded yet.</Text>
                        </Flex>
                      )}
                    </VStack>
                  </CardBody>
                </Card>
              )}

              {/* 4. Activity Timeline Linear Trend Chart */}
              <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl" shadow="sm" position="relative">
                <CardBody p={5}>
                  <Flex justify="space-between" align="center" mb={3}>
                    <HStack spacing={2}>
                      <Box p={2} bg="linear-gradient(135deg, rgba(14, 165, 233, 0.12) 0%, rgba(16, 185, 129, 0.12) 100%)" color="#0284c7" borderRadius="lg">
                        <FontAwesomeIcon icon={faChartLine} />
                      </Box>
                      <Heading size="xs" fontWeight="800">
                        Quiz Submissions Activity
                      </Heading>
                    </HStack>
                    <HStack spacing={1.5}>
                      <Badge colorScheme="cyan" variant="subtle" px={2} py={0.5} borderRadius="full" fontSize="3xs">
                        Trend Curve
                      </Badge>
                      <Tooltip label="Enlarge & Advanced Filter Tools">
                        <IconButton
                          aria-label="Enlarge Chart"
                          icon={<FontAwesomeIcon icon={faExpand} />}
                          size="xs"
                          variant="ghost"
                          colorScheme="cyan"
                          onClick={() => handleOpenEnlargeModal('activity')}
                        />
                      </Tooltip>
                    </HStack>
                  </Flex>

                  {summary?.activityTimeline && summary.activityTimeline.length > 0 ? (
                    <Box mt={1}>
                      <LinearTrendChart data={summary.activityTimeline} />
                      <Flex justify="space-between" align="center" mt={3} pt={2} borderTop="1px solid" borderColor={borderColor} fontSize="3xs" color={mutedText}>
                        <Text>Past 14 Days Activity</Text>
                        <Text fontWeight="bold" color="#10b981">
                          Peak: {Math.max(...summary.activityTimeline.map(t => t.attempts), 0)} submissions/day
                        </Text>
                      </Flex>
                    </Box>
                  ) : (
                    <Flex justify="center" align="center" py={10} color={mutedText}>
                      <Text fontSize="xs">No timeline activity logged yet.</Text>
                    </Flex>
                  )}
                </CardBody>
              </Card>
            </SimpleGrid>
          </Box>

          {/* Question Performance & Class Review (Interactive Error Rate Graph + Question Detail Tabs + Section Quiz Filter) */}
          <QuestionDifficultyDeck
            attempts={attempts}
            courseId={selectedCourseId}
            courseName={currentCourseMeta.name}
            isGlobal={isGlobal}
            courseSections={courseSections}
          />

          {/* Enrolled Students Table */}
          <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl" shadow="sm">
            <CardBody p={6}>
              {/* Table Header & Search Controls */}
              <Flex justify="space-between" align={{ base: 'start', md: 'center' }} mb={4} flexWrap="wrap" gap={3}>
                <Box>
                  <HStack spacing={2} align="center">
                    <Heading size="md" fontWeight="800">
                      Enrolled Students ({filteredStudents.length})
                    </Heading>
                    {studentTableQuizFilter !== 'all' && (
                      <Badge colorScheme="cyan" variant="solid" fontSize="3xs" borderRadius="md" px={2} py={0.5}>
                        Quiz Scope
                      </Badge>
                    )}
                  </HStack>
                  <Text fontSize="xs" color={mutedText} mt={0.5}>
                    {studentTableQuizFilter === 'all'
                      ? 'Active enrolled students • Performance and overall quiz attempt records'
                      : `Filtered performance records for: ${selectedStudentQuizLabel}`}
                  </Text>
                </Box>

                <HStack spacing={2.5} flexWrap="wrap">
                  {/* Quiz Scope Selector Menu */}
                  <Menu placement="bottom-end">
                    <MenuButton
                      as={Button}
                      size="sm"
                      variant="outline"
                      borderColor={studentTableQuizFilter !== 'all' ? 'cyan.400' : borderColor}
                      bg={studentTableQuizFilter !== 'all' ? 'rgba(14, 165, 233, 0.08)' : undefined}
                      borderRadius="lg"
                      fontSize="xs"
                      fontWeight="700"
                      leftIcon={<FontAwesomeIcon icon={faLayerGroup} color={studentTableQuizFilter !== 'all' ? '#0ea5e9' : '#94a3b8'} />}
                      rightIcon={<FontAwesomeIcon icon={faChevronDown} fontSize="9px" />}
                      maxW="220px"
                      isTruncated
                    >
                      {selectedStudentQuizLabel}
                    </MenuButton>
                    <MenuList zIndex={20} shadow="xl" borderRadius="xl" maxH="320px" overflowY="auto" fontSize="xs">
                      <MenuItem
                        fontWeight={studentTableQuizFilter === 'all' ? '800' : 'normal'}
                        color={studentTableQuizFilter === 'all' ? 'cyan.400' : undefined}
                        onClick={() => { setStudentTableQuizFilter('all'); setPage(1); }}
                      >
                        🌐 All Quizzes &amp; Sections (Overall)
                      </MenuItem>

                      {courseQuizzes.length > 0 && (
                        <>
                          <MenuDivider />
                          <Box px={3} py={1} fontSize="3xs" fontWeight="800" textTransform="uppercase" color={mutedText}>
                            Overall Course Quiz
                          </Box>
                          {courseQuizzes.map(q => (
                            <MenuItem
                              key={q.quizId}
                              fontWeight={studentTableQuizFilter === q.quizId ? '800' : 'normal'}
                              color={studentTableQuizFilter === q.quizId ? 'cyan.400' : undefined}
                              onClick={() => { setStudentTableQuizFilter(q.quizId); setPage(1); }}
                            >
                              📘 {q.title} ({q.attemptCount} att)
                            </MenuItem>
                          ))}
                        </>
                      )}

                      {sectionQuizzes.length > 0 && (
                        <>
                          <MenuDivider />
                          <Box px={3} py={1} fontSize="3xs" fontWeight="800" textTransform="uppercase" color={mutedText}>
                            Section-Specific Quizzes ({sectionQuizzes.length})
                          </Box>
                          {sectionQuizzes.map(q => (
                            <MenuItem
                              key={q.quizId}
                              fontWeight={studentTableQuizFilter === q.quizId ? '800' : 'normal'}
                              color={studentTableQuizFilter === q.quizId ? 'teal.400' : undefined}
                              onClick={() => { setStudentTableQuizFilter(q.quizId); setPage(1); }}
                            >
                              📑 [{q.chapterName}] {q.title} ({q.attemptCount} att)
                            </MenuItem>
                          ))}
                        </>
                      )}
                    </MenuList>
                  </Menu>

                  <InputGroup size="sm" w={{ base: '100%', md: '200px' }}>
                    <InputLeftElement pointerEvents="none">
                      <FontAwesomeIcon icon={faSearch} color="#94a3b8" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search student, email, id..."
                      borderRadius="lg"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setPage(1);
                      }}
                    />
                  </InputGroup>

                  <Select
                    size="sm"
                    w="130px"
                    borderRadius="lg"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="All">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Completed">Completed / Passed</option>
                    {studentTableQuizFilter !== 'all' && (
                      <option value="Not Attempted">Not Attempted</option>
                    )}
                  </Select>

                  <Select
                    size="sm"
                    w="145px"
                    borderRadius="lg"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="lastActive">Sort: Recent</option>
                    <option value="lastAttempt">Sort: Last Attempt</option>
                    <option value="userName">Sort: Name</option>
                    <option value="avgScorePercentage">Sort: Score</option>
                    <option value="quizAttemptsCount">Sort: Attempts</option>
                  </Select>

                  <Select
                    size="sm"
                    w="105px"
                    borderRadius="lg"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    <option value={10}>10 / page</option>
                    <option value={15}>15 / page</option>
                    <option value={25}>25 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={-1}>Show All</option>
                  </Select>
                </HStack>
              </Flex>

              {/* Active Quiz Filter Bar */}
              {studentTableQuizFilter !== 'all' && (
                <Flex align="center" justify="space-between" bg={useColorModeValue('cyan.50', 'rgba(14, 165, 233, 0.08)')} border="1px solid" borderColor="cyan.400" px={3.5} py={2} borderRadius="xl" mb={4}>
                  <HStack spacing={2}>
                    <Badge colorScheme="cyan" variant="solid" borderRadius="full" px={2} py={0.5} fontSize="3xs">
                      FILTERED BY QUIZ
                    </Badge>
                    <Text fontSize="xs" fontWeight="700">
                      Showing results specifically for: <Text as="span" color="#0ea5e9">{selectedStudentQuizLabel}</Text>
                    </Text>
                  </HStack>
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="cyan"
                    leftIcon={<FontAwesomeIcon icon={faTimes} />}
                    onClick={() => { setStudentTableQuizFilter('all'); setPage(1); }}
                    fontSize="2xs"
                  >
                    Reset to All Quizzes
                  </Button>
                </Flex>
              )}

              {/* Table */}
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead bg={useColorModeValue('gray.50', 'whiteAlpha.50')}>
                    <Tr>
                      <Th fontSize="2xs" textTransform="uppercase">Student</Th>
                      <Th fontSize="2xs" textTransform="uppercase">User ID</Th>
                      {isGlobal && <Th fontSize="2xs" textTransform="uppercase">Course</Th>}
                      <Th fontSize="2xs" textTransform="uppercase">Enrolled Date</Th>
                      <Th fontSize="2xs" textTransform="uppercase">Last Attempt</Th>
                      <Th fontSize="2xs" textTransform="uppercase">Status</Th>
                      <Th fontSize="2xs" textTransform="uppercase">Quiz Attempts</Th>
                      <Th fontSize="2xs" textTransform="uppercase">Avg Score</Th>
                      <Th fontSize="2xs" textTransform="uppercase">Highest Score</Th>
                      <Th fontSize="2xs" textTransform="uppercase">Pass Rate</Th>
                      <Th fontSize="2xs" textTransform="uppercase" textAlign="right">Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paginatedStudents.length > 0 ? (
                      paginatedStudents.map((s) => (
                        <Tr
                          key={`${s.courseId}_${s.userId}`}
                          _hover={{ bg: tableHoverBg }}
                          transition="background 0.15s"
                          cursor="pointer"
                          onClick={() => handleViewStudentHistory(s)}
                        >
                          <Td py={3.5}>
                            <HStack spacing={3}>
                              <Avatar
                                size="sm"
                                name={s.userName}
                                src={`https://api.dicebear.com/7.x/adventurer/svg?seed=${encodeURIComponent(s.userEmail || s.userName)}`}
                                border="1px solid"
                                borderColor="emerald.200"
                              />
                              <Box>
                                <HStack spacing={1.5} align="center">
                                  <Text fontWeight="700" fontSize="xs">
                                    {s.userName}
                                  </Text>
                                  {isCurrentLoggedInUser(s.userEmail) && (
                                    <Badge bg="linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)" color="white" fontWeight="800" fontSize="3xs" px={2} py={0.5} borderRadius="full" shadow="0 0 8px rgba(16, 185, 129, 0.4)">
                                      YOU
                                    </Badge>
                                  )}
                                </HStack>
                                <Text fontSize="2xs" color={mutedText}>
                                  {s.userEmail}
                                </Text>
                              </Box>
                            </HStack>
                          </Td>

                          <Td>
                            <HStack spacing={1}>
                              <Text fontSize="2xs" fontFamily="mono" color={mutedText}>
                                {s.userId.slice(0, 14)}...
                              </Text>
                              <IconButton
                                aria-label="Copy User ID"
                                icon={<FontAwesomeIcon icon={faCopy} />}
                                size="2xs"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyToClipboard(s.userId, 'User ID');
                                }}
                              />
                            </HStack>
                          </Td>

                          {isGlobal && (
                            <Td>
                              <Badge colorScheme="teal" variant="subtle" fontSize="3xs" px={2} borderRadius="md">
                                {s.courseName || s.courseId.slice(0, 8)}
                              </Badge>
                            </Td>
                          )}

                          <Td>
                            <Text fontSize="xs">
                              {s.downloadedAt ? new Date(s.downloadedAt).toLocaleDateString() : '-'}
                            </Text>
                            <Badge fontSize="3xs" colorScheme="gray" variant="outline">
                              {s.appVersion || 'v1.0.4'}
                            </Badge>
                          </Td>

                          <Td>
                            {s.lastAttemptTimestamp ? (
                              <Box>
                                <Text fontSize="xs" fontWeight="700">
                                  {new Date(s.lastAttemptTimestamp).toLocaleDateString()}
                                </Text>
                                <Text fontSize="3xs" color={mutedText}>
                                  {new Date(s.lastAttemptTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </Text>
                              </Box>
                            ) : (
                              <Badge fontSize="3xs" colorScheme="gray" variant="subtle" borderRadius="md">
                                No attempts
                              </Badge>
                            )}
                          </Td>

                          <Td>
                            {studentTableQuizFilter !== 'all' && (!s.quizAttemptsCount || s.quizAttemptsCount === 0) ? (
                              <Badge colorScheme="gray" variant="subtle" px={2} py={0.5} borderRadius="full" fontSize="3xs">
                                Not Attempted
                              </Badge>
                            ) : (
                              <Badge
                                colorScheme={s.status === 'Completed' || (s.passRate || 0) >= 70 ? 'cyan' : s.status === 'Active' ? 'green' : 'gray'}
                                px={2}
                                py={0.5}
                                borderRadius="full"
                                fontSize="3xs"
                              >
                                {s.status}
                              </Badge>
                            )}
                          </Td>

                          <Td>
                            <Text fontSize="xs" fontWeight="700" color={studentTableQuizFilter !== 'all' && (!s.quizAttemptsCount || s.quizAttemptsCount === 0) ? mutedText : undefined}>
                              {s.quizAttemptsCount || 0}
                            </Text>
                          </Td>

                          <Td w="140px">
                            {studentTableQuizFilter !== 'all' && (!s.quizAttemptsCount || s.quizAttemptsCount === 0) ? (
                              <Text fontSize="xs" color={mutedText}>&mdash;</Text>
                            ) : (
                              <>
                                <Flex justify="space-between" fontSize="2xs" fontWeight="bold" mb={1}>
                                  <Text>{s.avgScorePercentage || 0}%</Text>
                                </Flex>
                                <Progress
                                  value={s.avgScorePercentage || 0}
                                  size="xs"
                                  borderRadius="full"
                                  colorScheme={(s.avgScorePercentage || 0) >= 70 ? 'green' : 'orange'}
                                />
                              </>
                            )}
                          </Td>

                          <Td>
                            {studentTableQuizFilter !== 'all' && (!s.quizAttemptsCount || s.quizAttemptsCount === 0) ? (
                              <Text fontSize="xs" color={mutedText}>&mdash;</Text>
                            ) : (
                              <Text fontSize="xs" fontWeight="700" color="#10b981">
                                {s.highestScore || s.avgScorePercentage || 0}%
                              </Text>
                            )}
                          </Td>

                          <Td>
                            {studentTableQuizFilter !== 'all' && (!s.quizAttemptsCount || s.quizAttemptsCount === 0) ? (
                              <Badge colorScheme="gray" variant="subtle" borderRadius="md" px={2} fontSize="3xs">
                                &mdash;
                              </Badge>
                            ) : (
                              <Badge
                                colorScheme={(s.passRate || 0) >= 70 ? 'green' : (s.passRate || 0) > 0 ? 'yellow' : 'gray'}
                                borderRadius="md"
                                px={2}
                                fontSize="3xs"
                              >
                                {s.passRate || 0}%
                              </Badge>
                            )}
                          </Td>

                          <Td textAlign="right">
                            <HStack spacing={1} justify="flex-end">
                              <Tooltip label={`Send email notification to ${s.userName}`}>
                                <IconButton
                                  aria-label="Send Email"
                                  icon={<FontAwesomeIcon icon={faEnvelope} />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="teal"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenEmailModal(s);
                                  }}
                                />
                              </Tooltip>
                              <Button
                                size="xs"
                                variant="ghost"
                                colorScheme="green"
                                rightIcon={<FontAwesomeIcon icon={faChevronRight} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewStudentHistory(s);
                                }}
                              >
                                History
                              </Button>
                            </HStack>
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={10} textAlign="center" py={10} color={mutedText}>
                          No students matching your search criteria.
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>

              {/* Pagination */}
              {totalPages > 1 && (
                <Flex justify="space-between" align="center" mt={6} pt={4} borderTop="1px solid" borderColor={borderColor}>
                  <Text fontSize="xs" color={mutedText}>
                    Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, filteredStudents.length)} of {filteredStudents.length} students
                  </Text>
                  <HStack spacing={2}>
                    <Button size="xs" isDisabled={page === 1} onClick={() => setPage(page - 1)}>
                      Previous
                    </Button>
                    <Text fontSize="xs" fontWeight="bold" px={2}>
                      Page {page} of {totalPages}
                    </Text>
                    <Button size="xs" isDisabled={page === totalPages} onClick={() => setPage(page + 1)}>
                      Next
                    </Button>
                  </HStack>
                </Flex>
              )}
            </CardBody>
          </Card>
        </VStack>
      )}

      {/* Advanced Fullscreen / Enlarged Chart Modal (Tailored High-Resolution View) */}
      <Modal isOpen={isEnlargeModalOpen} onClose={onEnlargeModalClose} size="5xl" isCentered scrollBehavior="inside">
        <ModalOverlay backdropFilter="blur(8px)" bg="rgba(0,0,0,0.7)" />
        <ModalContent bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl" shadow="2xl" maxW="1020px">
          <ModalHeader pb={3}>
            <Flex justify="space-between" align="center" pr={8} flexWrap="wrap" gap={3}>
              <HStack spacing={3}>
                <Box p={2.5} bg="linear-gradient(135deg, rgba(14, 165, 233, 0.12) 0%, rgba(16, 185, 129, 0.12) 100%)" color="#0284c7" borderRadius="xl">
                  <FontAwesomeIcon icon={enlargedChartType === 'mastery' ? faChartPie : enlargedChartType === 'intake' ? faCalendarAlt : enlargedChartType === 'chapters' ? faBookOpen : enlargedChartType === 'comparison' ? faGlobe : faChartLine} />
                </Box>
                <Box>
                  <HStack spacing={2} align="center">
                    <Heading size="md" fontWeight="800">
                      {enlargedChartType === 'mastery' && 'Mastery Breakdown & Score Distribution'}
                      {enlargedChartType === 'intake' && 'Monthly Student Intake & Growth Velocity'}
                      {enlargedChartType === 'chapters' && 'Quiz & Chapter Pass Rate Metrics'}
                      {enlargedChartType === 'activity' && 'Quiz Submissions Activity & Timeline Curve'}
                      {enlargedChartType === 'comparison' && 'Platform Courses Comparative Analysis'}
                    </Heading>
                    <Badge bg="#0B192C" color="#10B981" border="1px solid rgba(16, 185, 129, 0.4)" borderRadius="full" px={2} py={0.5} fontSize="3xs">
                      ENLARGED 2D VIEW
                    </Badge>
                  </HStack>
                  <Text fontSize="xs" color={mutedText} mt={0.5}>
                    Interactive Deep-Dive & Performance Breakdown &bull; {currentCourseMeta.name}
                  </Text>
                </Box>
              </HStack>

              {/* Clean Custom Nav Pills in Modal Header */}
              <HStack spacing={1.5} bg={useColorModeValue('gray.100', 'whiteAlpha.100')} p={1} borderRadius="xl">
                {(['mastery', 'intake', isGlobal ? 'comparison' : 'chapters', 'activity'] as const).map((typeKey) => {
                  const label = typeKey === 'mastery' ? 'Mastery' : typeKey === 'intake' ? 'Intake' : typeKey === 'activity' ? 'Activity' : isGlobal ? 'Courses' : 'Chapters';
                  const isSelected = enlargedChartType === typeKey;
                  return (
                    <Button
                      key={typeKey}
                      size="xs"
                      borderRadius="lg"
                      variant="ghost"
                      bg={isSelected ? 'linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)' : 'transparent'}
                      color={isSelected ? 'white' : mutedText}
                      _hover={{ bg: isSelected ? '#059669' : useColorModeValue('gray.200', 'whiteAlpha.200') }}
                      fontWeight="700"
                      onClick={() => setEnlargedChartType(typeKey as any)}
                    >
                      {label}
                    </Button>
                  );
                })}
              </HStack>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py={4}>
            {/* Filter & Sorting Controls Toolbar */}
            <Flex
              justify="space-between"
              align="center"
              p={3}
              mb={5}
              bg={useColorModeValue('gray.50', 'whiteAlpha.50')}
              borderRadius="xl"
              border="1px solid"
              borderColor={borderColor}
              flexWrap="wrap"
              gap={3}
            >
              <HStack spacing={2} align="center">
                <Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={mutedText}>
                  Date Range:
                </Text>
                <HStack spacing={1}>
                  {(['all', '7d', '14d', '30d', '90d'] as const).map((range) => {
                    const isRangeActive = enlargedDateFilter === range;
                    return (
                      <Button
                        key={range}
                        size="2xs"
                        borderRadius="md"
                        variant="ghost"
                        bg={isRangeActive ? 'linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)' : 'transparent'}
                        color={isRangeActive ? 'white' : mutedText}
                        _hover={{ bg: isRangeActive ? '#059669' : useColorModeValue('gray.200', 'whiteAlpha.200') }}
                        textTransform="uppercase"
                        fontWeight="700"
                        px={2.5}
                        onClick={() => setEnlargedDateFilter(range)}
                      >
                        {range === 'all' ? 'All Time' : range}
                      </Button>
                    );
                  })}
                </HStack>
              </HStack>

              <HStack spacing={3}>
                <HStack spacing={1.5} align="center">
                  <Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={mutedText}>
                    Metric:
                  </Text>
                  <Select
                    size="xs"
                    w="120px"
                    borderRadius="md"
                    value={enlargedMetric}
                    onChange={(e) => setEnlargedMetric(e.target.value as any)}
                  >
                    <option value="attempts">Attempts</option>
                    <option value="score">Avg Score %</option>
                    <option value="passRate">Pass Rate %</option>
                  </Select>
                </HStack>

                <HStack spacing={1.5} align="center">
                  <Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={mutedText}>
                    Sort:
                  </Text>
                  <Select
                    size="xs"
                    w="130px"
                    borderRadius="md"
                    value={enlargedSort}
                    onChange={(e) => setEnlargedSort(e.target.value as any)}
                  >
                    <option value="desc">High to Low</option>
                    <option value="asc">Low to High</option>
                  </Select>
                </HStack>
              </HStack>
            </Flex>

            {/* High-Resolution Enlarged Canvas Container */}
            <Box
              p={6}
              bg={useColorModeValue('white', 'gray.900')}
              borderRadius="2xl"
              border="1px solid"
              borderColor={borderColor}
              shadow="sm"
              mb={6}
            >
              {enlargedChartType === 'intake' && (
                <Box>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Box>
                      <Text fontSize="sm" fontWeight="800">
                        {enlargedDateFilter === '7d' || enlargedDateFilter === '14d' ? 'Daily Cohort Admissions & Intake' : 'Monthly Student Cohort Admissions & Enrollment Velocity'}
                      </Text>
                      <Text fontSize="2xs" color={mutedText}>
                        Calculated from student registration timestamps &bull; Range: {enlargedDateFilter === 'all' ? 'All Time' : `Past ${enlargedDateFilter}`}
                      </Text>
                    </Box>
                    <Badge colorScheme="green" px={2.5} py={1} borderRadius="full" fontSize="xs">
                      +{enlargedModalData.totalEnrolled} Enrolled ({enlargedDateFilter === 'all' ? 'All Time' : enlargedDateFilter})
                    </Badge>
                  </Flex>

                  {/* Clean Widescreen 2D SVG Graph */}
                  <Enlarged2DMonthlyIntakeChart data={enlargedModalData.monthlyIntake} />

                  {/* Monthly Breakdown Data Cards */}
                  {enlargedModalData.monthlyIntake.length > 0 && (
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mt={5} pt={4} borderTop="1px solid" borderColor={borderColor}>
                      {enlargedModalData.monthlyIntake.map((m, idx, arr) => {
                        const prevCount = idx > 0 ? arr[idx - 1].enrolled : m.enrolled;
                        const growth = prevCount > 0 ? Math.round(((m.enrolled - prevCount) / prevCount) * 100) : 0;
                        const share = enlargedModalData.totalEnrolled > 0 ? Math.round((m.enrolled / enlargedModalData.totalEnrolled) * 100) : 0;

                        return (
                          <Box key={m.month} p={3} borderRadius="xl" bg={useColorModeValue('gray.50', 'whiteAlpha.50')} border="1px solid" borderColor={borderColor}>
                            <Text fontSize="3xs" color={mutedText} fontWeight="700" textTransform="uppercase">
                              {m.month}
                            </Text>
                            <HStack justify="space-between" align="baseline" mt={1}>
                              <Heading size="md" color="#10b981">
                                +{m.enrolled}
                              </Heading>
                              <Badge colorScheme={growth >= 0 ? 'green' : 'orange'} fontSize="3xs">
                                {growth >= 0 ? `+${growth}%` : `${growth}%`}
                              </Badge>
                            </HStack>
                            <Text fontSize="3xs" color={mutedText} mt={1}>
                              {share}% of cohort
                            </Text>
                          </Box>
                        );
                      })}
                    </SimpleGrid>
                  )}
                </Box>
              )}

              {enlargedChartType === 'mastery' && (
                <Box>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Box>
                      <Text fontSize="sm" fontWeight="800">
                        Score Mastery Tiers & Grade Distribution Analysis
                      </Text>
                      <Text fontSize="2xs" color={mutedText}>
                        Distribution across all {enlargedModalData.totalAttempts} quiz attempt submissions &bull; Range: {enlargedDateFilter === 'all' ? 'All Time' : `Past ${enlargedDateFilter}`}
                      </Text>
                    </Box>
                    <Badge colorScheme="teal" px={2.5} py={1} borderRadius="full" fontSize="xs">
                      {enlargedModalData.averageScore}% Class Avg
                    </Badge>
                  </Flex>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} alignItems="center">
                    <Flex justify="center" align="center" py={4}>
                      <DonutChart data={enlargedModalData.masteryBreakdown} total={enlargedModalData.totalAttempts} size={220} />
                    </Flex>

                    <VStack align="stretch" spacing={3.5}>
                      {enlargedModalData.scoreDistribution.map((b) => {
                        const totalAtt = enlargedModalData.totalAttempts || 1;
                        const pct = Math.round((b.count / totalAtt) * 100);
                        return (
                          <Box key={b.range}>
                            <Flex justify="space-between" fontSize="xs" fontWeight="700" mb={1}>
                              <Text>{b.range} Tier</Text>
                              <Text color="#10b981">
                                {b.count} attempts ({pct}%)
                              </Text>
                            </Flex>
                            <Progress
                              value={pct}
                              size="sm"
                              borderRadius="full"
                              colorScheme={b.range.includes('81') ? 'green' : b.range.includes('61') ? 'teal' : b.range.includes('41') ? 'yellow' : 'red'}
                            />
                          </Box>
                        );
                      })}
                    </VStack>
                  </SimpleGrid>
                </Box>
              )}

              {enlargedChartType === 'activity' && (
                <Box>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Box>
                      <Text fontSize="sm" fontWeight="800">
                        Submission Activity Timeline & Trend Curve
                      </Text>
                      <Text fontSize="2xs" color={mutedText}>
                        Plotted by {enlargedMetric === 'score' ? 'Daily Avg Score %' : enlargedMetric === 'passRate' ? 'Daily Pass Rate %' : 'Daily Submission Count'} &bull; Range: {enlargedDateFilter === 'all' ? 'All Time' : `Past ${enlargedDateFilter}`}
                      </Text>
                    </Box>
                    <Badge colorScheme={enlargedMetric === 'score' ? 'green' : enlargedMetric === 'passRate' ? 'cyan' : 'teal'} px={2.5} py={1} borderRadius="full" fontSize="xs">
                      {enlargedModalData.totalAttempts} Total Attempts
                    </Badge>
                  </Flex>
                  <LinearTrendChart data={enlargedModalData.activityTimeline} isEnlarged={true} metric={enlargedMetric} />
                </Box>
              )}

              {(enlargedChartType === 'chapters' || enlargedChartType === 'comparison') && (
                <Box>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Box>
                      <Text fontSize="sm" fontWeight="800">
                        {isGlobal ? 'Course Performance & Enrollment Comparisons' : 'Chapter & Topic Mastery Pass Rate Breakdown'}
                      </Text>
                      <Text fontSize="2xs" color={mutedText}>
                        Ranked by {enlargedMetric === 'score' ? 'Average Score %' : enlargedMetric === 'passRate' ? 'Pass Rate %' : 'Total Attempts'} ({enlargedSort === 'desc' ? 'High to Low' : 'Low to High'}) &bull; Range: {enlargedDateFilter === 'all' ? 'All Time' : `Past ${enlargedDateFilter}`}
                      </Text>
                    </Box>
                    <Badge colorScheme="cyan" px={2.5} py={1} borderRadius="full" fontSize="xs">
                      {enlargedModalData.overallPassRate}% Benchmark Pass Rate
                    </Badge>
                  </Flex>

                  <VStack spacing={3} align="stretch">
                    {(isGlobal ? enlargedModalData.courseComparison : enlargedModalData.quizBreakdown).length > 0 ? (
                      (isGlobal ? enlargedModalData.courseComparison : enlargedModalData.quizBreakdown).map((item: any) => (
                        <Box key={item.courseId || item.quizId} p={3.5} borderRadius="xl" bg={useColorModeValue('gray.50', 'whiteAlpha.50')} border="1px solid" borderColor={borderColor}>
                          <Flex justify="space-between" align="center" mb={2}>
                            <Box>
                              <Text fontSize="sm" fontWeight="800">
                                {item.courseName || item.title}
                              </Text>
                              <Text fontSize="2xs" color={mutedText}>
                                {item.enrolled ? `${item.enrolled} enrolled students` : item.chapter} &bull; {item.attempts} attempts
                              </Text>
                            </Box>
                            <HStack spacing={3}>
                              <Badge colorScheme={item.passRate >= 70 ? 'green' : 'orange'} fontSize="xs" px={2} py={0.5} borderRadius="md">
                                {item.passRate}% Pass Rate
                              </Badge>
                              <Text fontSize="sm" fontWeight="800" color="#10b981">
                                {item.avgScore}% Avg
                              </Text>
                            </HStack>
                          </Flex>
                          <Progress value={item.passRate} size="sm" borderRadius="full" colorScheme="green" />
                        </Box>
                      ))
                    ) : (
                      <Flex justify="center" align="center" py={8} color={mutedText}>
                        <Text fontSize="xs">No chapter assessment records found for this timeframe.</Text>
                      </Flex>
                    )}
                  </VStack>
                </Box>
              )}
            </Box>

            {/* Quick Stat Summary Chips */}
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
              <Box p={3} bg={useColorModeValue('gray.50', 'whiteAlpha.50')} borderRadius="xl" border="1px solid" borderColor={borderColor} textAlign="center">
                <Text fontSize="3xs" color={mutedText} fontWeight="700" textTransform="uppercase">Enrolled Learners</Text>
                <Text fontSize="lg" fontWeight="800">{enlargedModalData.totalEnrolled}</Text>
              </Box>
              <Box p={3} bg={useColorModeValue('gray.50', 'whiteAlpha.50')} borderRadius="xl" border="1px solid" borderColor={borderColor} textAlign="center">
                <Text fontSize="3xs" color={mutedText} fontWeight="700" textTransform="uppercase">Active Students</Text>
                <Text fontSize="lg" fontWeight="800" color="blue.500">{enlargedModalData.activeStudents}</Text>
              </Box>
              <Box p={3} bg={useColorModeValue('gray.50', 'whiteAlpha.50')} borderRadius="xl" border="1px solid" borderColor={borderColor} textAlign="center">
                <Text fontSize="3xs" color={mutedText} fontWeight="700" textTransform="uppercase">Average Score</Text>
                <Text fontSize="lg" fontWeight="800" color="#10b981">{enlargedModalData.averageScore}%</Text>
              </Box>
              <Box p={3} bg={useColorModeValue('gray.50', 'whiteAlpha.50')} borderRadius="xl" border="1px solid" borderColor={borderColor} textAlign="center">
                <Text fontSize="3xs" color={mutedText} fontWeight="700" textTransform="uppercase">Pass Rate</Text>
                <Text fontSize="lg" fontWeight="800" color="cyan.500">{enlargedModalData.overallPassRate}%</Text>
              </Box>
            </SimpleGrid>
          </ModalBody>

          <ModalFooter borderTop="1px solid" borderColor={borderColor} gap={3}>
            <Button size="sm" variant="ghost" onClick={onEnlargeModalClose}>
              Close
            </Button>
            <Button
              size="sm"
              colorScheme="green"
              leftIcon={<FontAwesomeIcon icon={faDownload} />}
              onClick={() => handleDownloadPDF(true)}
            >
              Export PDF Report
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Student Quiz History Drill-down Modal (Querying GSI_User_History) */}
      <Modal isOpen={isHistoryOpen} onClose={onHistoryClose} size="4xl" isCentered scrollBehavior="inside">
        <ModalOverlay backdropFilter="blur(6px)" bg="rgba(0,0,0,0.6)" />
        <ModalContent bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl" shadow="2xl" maxH="90vh">
          <ModalHeader pb={3} borderBottom="1px solid" borderColor={borderColor}>
            <Flex justify="space-between" align="center" pr={8}>
              <HStack spacing={3}>
                <Avatar
                  size="md"
                  name={selectedStudent?.userName}
                  src={`https://api.dicebear.com/7.x/adventurer/svg?seed=${encodeURIComponent(selectedStudent?.userEmail || selectedStudent?.userName || '')}`}
                  border="2px solid"
                  borderColor="cyan.400"
                />
                <Box>
                  <HStack spacing={2} align="center">
                    <Heading size="sm" fontWeight="800">
                      {selectedStudent?.userName} &mdash; Quiz Performance
                    </Heading>
                    <Badge colorScheme="cyan" variant="subtle" fontSize="2xs" borderRadius="md">
                      Student Analytics
                    </Badge>
                  </HStack>
                  <Text fontSize="xs" color={mutedText} mt={0.5}>
                    User ID: {selectedStudent?.userId} &bull; {selectedStudent?.userEmail || 'Active Student'}
                  </Text>
                </Box>
              </HStack>
            </Flex>
          </ModalHeader>
          <ModalCloseButton top={4} right={4} />

          <ModalBody py={4} px={6}>
            {/* Student Aggregate Stat Highlights */}
            {selectedStudent && (
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={5}>
                <Box p={3.5} bg={useColorModeValue('gray.50', 'whiteAlpha.50')} borderRadius="xl" border="1px solid" borderColor={borderColor} textAlign="center">
                  <Text fontSize="2xs" color={mutedText} textTransform="uppercase" fontWeight="bold">Total Attempts</Text>
                  <Text fontSize="xl" fontWeight="800" color={useColorModeValue('gray.800', 'white')}>{studentAggregateStats.totalAttempts}</Text>
                  <Text fontSize="3xs" color={mutedText}>{studentHistory.length} recorded tests</Text>
                </Box>
                <Box p={3.5} bg={useColorModeValue('gray.50', 'whiteAlpha.50')} borderRadius="xl" border="1px solid" borderColor={borderColor} textAlign="center">
                  <Text fontSize="2xs" color={mutedText} textTransform="uppercase" fontWeight="bold">Average Score</Text>
                  <Text fontSize="xl" fontWeight="800" color="#10b981">{studentAggregateStats.avgScore}%</Text>
                  <Text fontSize="3xs" color="#10b981">{studentAggregateStats.passRate}% pass rate</Text>
                </Box>
                <Box p={3.5} bg={useColorModeValue('gray.50', 'whiteAlpha.50')} borderRadius="xl" border="1px solid" borderColor={borderColor} textAlign="center">
                  <Text fontSize="2xs" color={mutedText} textTransform="uppercase" fontWeight="bold">Highest Score</Text>
                  <Text fontSize="xl" fontWeight="800" color="cyan.400">{studentAggregateStats.highestScore}%</Text>
                  <Text fontSize="3xs" color="cyan.400">Personal best</Text>
                </Box>
                <Box p={3.5} bg={useColorModeValue('gray.50', 'whiteAlpha.50')} borderRadius="xl" border="1px solid" borderColor={borderColor} textAlign="center">
                  <Text fontSize="2xs" color={mutedText} textTransform="uppercase" fontWeight="bold">Questions Taken</Text>
                  <Text fontSize="xl" fontWeight="800" color="purple.300">{studentAggregateStats.totalQuestions}</Text>
                  <Text fontSize="3xs" color={mutedText}>{studentAggregateStats.overallAccuracy}% overall accuracy</Text>
                </Box>
              </SimpleGrid>
            )}

            {historyLoading ? (
              <Flex justify="center" align="center" py={16} flexDir="column" gap={3}>
                <Spinner color="#10b981" size="lg" />
                <Text fontSize="sm" color={mutedText}>Loading student quiz records & chart data...</Text>
              </Flex>
            ) : studentHistory.length === 0 ? (
              <Flex justify="center" align="center" py={12} flexDir="column" gap={2} color={mutedText}>
                <FontAwesomeIcon icon={faHistory} size="2x" />
                <Text fontSize="sm" fontWeight="600">No quiz attempts recorded for this student yet.</Text>
              </Flex>
            ) : (
              <Tabs index={historyActiveTab} onChange={setHistoryActiveTab} variant="soft-rounded" colorScheme="cyan">
                <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
                  <TabList bg={useColorModeValue('gray.100', 'whiteAlpha.100')} p={1} borderRadius="xl">
                    <Tab fontSize="xs" fontWeight="700" py={1.5} px={3} borderRadius="lg">
                      <FontAwesomeIcon icon={faChartLine} style={{ marginRight: '6px' }} />
                      Performance Graph & Trends
                    </Tab>
                    <Tab fontSize="xs" fontWeight="700" py={1.5} px={3} borderRadius="lg">
                      <FontAwesomeIcon icon={faHistory} style={{ marginRight: '6px' }} />
                      Attempt History Log ({studentHistory.length})
                    </Tab>
                  </TabList>

                  <HStack spacing={2} align="center" flexWrap="wrap">
                    {/* Quiz Selector in Student Drilldown */}
                    {studentAttemptedQuizzes.length > 1 && (
                      <Menu placement="bottom-end">
                        <MenuButton
                          as={Button}
                          size="xs"
                          variant="outline"
                          borderColor={historyQuizFilter !== 'all' ? '#0ea5e9' : borderColor}
                          bg={historyQuizFilter !== 'all' ? 'rgba(14, 165, 233, 0.12)' : undefined}
                          borderRadius="full"
                          fontSize="2xs"
                          fontWeight="700"
                          leftIcon={<FontAwesomeIcon icon={faLayerGroup} color={historyQuizFilter !== 'all' ? '#0ea5e9' : '#94a3b8'} />}
                          rightIcon={<FontAwesomeIcon icon={faChevronDown} fontSize="8px" />}
                          maxW="190px"
                          isTruncated
                        >
                          {historyQuizFilter === 'all'
                            ? `All Quizzes (${studentHistory.length})`
                            : (studentAttemptedQuizzes.find(q => q.quizId === historyQuizFilter)?.title || 'Selected Quiz')}
                        </MenuButton>
                        <MenuList zIndex={30} shadow="xl" borderRadius="xl" maxH="260px" overflowY="auto" fontSize="xs">
                          <MenuItem
                            fontWeight={historyQuizFilter === 'all' ? '800' : 'normal'}
                            color={historyQuizFilter === 'all' ? 'cyan.400' : undefined}
                            onClick={() => setHistoryQuizFilter('all')}
                          >
                            🌐 All Quizzes ({studentHistory.length} attempts)
                          </MenuItem>
                          <MenuDivider />
                          {studentAttemptedQuizzes.map(q => (
                            <MenuItem
                              key={q.quizId}
                              fontWeight={historyQuizFilter === q.quizId ? '800' : 'normal'}
                              color={historyQuizFilter === q.quizId ? 'cyan.400' : undefined}
                              onClick={() => setHistoryQuizFilter(q.quizId)}
                            >
                              📝 {q.title} ({q.count} att)
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Menu>
                    )}

                    {/* Question Length Filter Pills */}
                    {availableQuestionCounts.length > 1 && (
                      <HStack spacing={1.5} align="center">
                        <Text fontSize="2xs" color={mutedText} fontWeight="bold" textTransform="uppercase" mr={1}>
                          <FontAwesomeIcon icon={faFilter} style={{ marginRight: '4px' }} />
                          Quiz Size:
                        </Text>
                        <Button
                          size="xs"
                          variant={historyQuestionFilter === 'all' ? 'solid' : 'outline'}
                          colorScheme={historyQuestionFilter === 'all' ? 'cyan' : 'gray'}
                          borderRadius="full"
                          onClick={() => setHistoryQuestionFilter('all')}
                          fontSize="2xs"
                          px={2.5}
                        >
                          All Sizes
                        </Button>
                        {availableQuestionCounts.map(qCount => {
                          const countForSize = (historyQuizFilter === 'all'
                            ? studentHistory
                            : studentHistory.filter(att => att.quizId === historyQuizFilter || (historyQuizFilter.endsWith('-quiz') && (!att.quizId || att.quizId === historyQuizFilter)))
                          ).filter(a => a.totalQuestions === qCount).length;

                          return (
                            <Button
                              key={qCount}
                              size="xs"
                              variant={historyQuestionFilter === qCount ? 'solid' : 'outline'}
                              colorScheme={historyQuestionFilter === qCount ? 'cyan' : 'gray'}
                              borderRadius="full"
                              onClick={() => setHistoryQuestionFilter(qCount)}
                              fontSize="2xs"
                              px={2.5}
                            >
                              {qCount}Q ({countForSize})
                            </Button>
                          );
                        })}
                      </HStack>
                    )}
                  </HStack>
                </Flex>

                <TabPanels>
                  {/* TAB 1: Graph & Trends */}
                  <TabPanel p={0}>
                    <VStack spacing={5} align="stretch">
                      {/* Graphical Score Timeline Chart */}
                      <Box
                        p={4}
                        bg={useColorModeValue('white', 'whiteAlpha.50')}
                        border="1px solid"
                        borderColor={borderColor}
                        borderRadius="xl"
                      >
                        <Flex justify="space-between" align="center" mb={3} flexWrap="wrap" gap={2}>
                          <Box>
                            <HStack spacing={2} align="center">
                              <FontAwesomeIcon icon={faArrowTrendUp} color="#10b981" />
                              <Text fontWeight="800" fontSize="sm">
                                Score Progression Over Time
                              </Text>
                            </HStack>
                            <Text fontSize="xs" color={mutedText}>
                              Chronological test score percentage across {filteredStudentHistory.length} attempt{filteredStudentHistory.length > 1 ? 's' : ''}
                              {historyQuestionFilter !== 'all' ? ` (${historyQuestionFilter}-question quizzes)` : ''}
                            </Text>
                          </Box>
                          <HStack spacing={3} fontSize="3xs" color={mutedText}>
                            <HStack spacing={1}>
                              <Box w="8px" h="8px" borderRadius="full" bg="#0ea5e9" />
                              <Text>Score %</Text>
                            </HStack>
                            <HStack spacing={1}>
                              <Box w="12px" h="1px" borderTop="2px dashed #10b981" />
                              <Text>70% Passing Line</Text>
                            </HStack>
                          </HStack>
                        </Flex>

                        {studentChartData.length > 0 ? (
                          <Box h="230px" w="100%">
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={studentChartData} margin={{ top: 10, right: 15, left: -20, bottom: 5 }}>
                                <defs>
                                  <linearGradient id="studentScoreGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.35} />
                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                                  </linearGradient>
                                  <linearGradient id="studentStrokeGrad" x1="0" y1="0" x2="1" y2="0">
                                    <stop offset="0%" stopColor="#0ea5e9" />
                                    <stop offset="100%" stopColor="#10b981" />
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                                <XAxis
                                  dataKey="shortLabel"
                                  stroke="#94a3b8"
                                  fontSize={11}
                                  tickLine={false}
                                />
                                <YAxis
                                  domain={[0, 100]}
                                  ticks={[0, 25, 50, 75, 100]}
                                  tickFormatter={(v) => `${v}%`}
                                  stroke="#94a3b8"
                                  fontSize={11}
                                  tickLine={false}
                                />
                                <RechartsTooltip
                                  content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                      const d = payload[0].payload;
                                      return (
                                        <Box
                                          bg="gray.900"
                                          border="1px solid"
                                          borderColor="whiteAlpha.300"
                                          p={3}
                                          borderRadius="xl"
                                          shadow="dark-lg"
                                          fontSize="xs"
                                          minW="220px"
                                        >
                                          <Flex justify="space-between" align="center" mb={1.5}>
                                            <Text fontWeight="800" color="white" fontSize="xs">
                                              {d.label}
                                            </Text>
                                            <Badge
                                              colorScheme={d.passed ? 'green' : 'red'}
                                              borderRadius="md"
                                              px={1.5}
                                              fontSize="3xs"
                                            >
                                              {d.passed ? 'PASSED' : 'NEEDS PRACTICE'}
                                            </Badge>
                                          </Flex>
                                          <Text fontSize="3xs" color="gray.400" mb={2}>
                                            {d.chapterName}
                                          </Text>
                                          <Divider borderColor="whiteAlpha.200" mb={2} />
                                          <VStack spacing={1} align="stretch">
                                            <Flex justify="space-between">
                                              <Text color="gray.400">Score:</Text>
                                              <Text fontWeight="bold" color={d.passed ? '#10b981' : '#f87171'}>
                                                {d.score} / {d.totalQuestions} ({d.percentage}%)
                                              </Text>
                                            </Flex>
                                            <Flex justify="space-between">
                                              <Text color="gray.400">Time Spent:</Text>
                                              <Text color="gray.200">{d.timeSpent}</Text>
                                            </Flex>
                                            <Flex justify="space-between">
                                              <Text color="gray.400">Date & Time:</Text>
                                              <Text color="gray.200">{d.fullDate}</Text>
                                            </Flex>
                                          </VStack>
                                        </Box>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                <ReferenceLine
                                  y={70}
                                  stroke="#10b981"
                                  strokeDasharray="4 4"
                                  label={{ value: 'Passing 70%', position: 'insideTopRight', fill: '#10b981', fontSize: 10 }}
                                />
                                <Area
                                  type="monotone"
                                  dataKey="percentage"
                                  stroke="url(#studentStrokeGrad)"
                                  strokeWidth={3}
                                  fillOpacity={1}
                                  fill="url(#studentScoreGrad)"
                                  dot={{ r: 4, fill: '#0ea5e9', stroke: '#fff', strokeWidth: 1.5 }}
                                  activeDot={{ r: 7, fill: '#10b981', stroke: '#fff', strokeWidth: 2 }}
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          </Box>
                        ) : (
                          <Flex justify="center" align="center" py={8} color={mutedText}>
                            <Text fontSize="xs">No attempts found matching the selected quiz size filter.</Text>
                          </Flex>
                        )}
                      </Box>

                      {/* Performance by Quiz Length (Variable 5Q / 10Q / 20Q breakdown) */}
                      {studentQuestionBreakdown.length > 0 && (
                        <Box>
                          <HStack spacing={2} align="center" mb={2.5}>
                            <FontAwesomeIcon icon={faLayerGroup} color="#0ea5e9" />
                            <Heading size="xs" fontWeight="800">
                              Performance by Quiz Length
                            </Heading>
                            <Text fontSize="2xs" color={mutedText}>
                              (Comparison across mobile custom question modes: 5Q, 10Q, 20Q)
                            </Text>
                          </HStack>

                          <SimpleGrid columns={{ base: 1, sm: 2, md: Math.min(studentQuestionBreakdown.length, 3) }} spacing={3}>
                            {studentQuestionBreakdown.map((breakdown) => (
                              <Card
                                key={breakdown.questionsPerQuiz}
                                variant="outline"
                                borderColor={borderColor}
                                borderRadius="xl"
                                p={3.5}
                                bg={useColorModeValue('white', 'whiteAlpha.50')}
                                shadow="xs"
                              >
                                <Flex justify="space-between" align="center" mb={2}>
                                  <Badge colorScheme="cyan" variant="subtle" px={2} py={0.5} borderRadius="md" fontSize="2xs" fontWeight="bold">
                                    {breakdown.questionsPerQuiz}-Question Mode
                                  </Badge>
                                  <Text fontSize="2xs" color={mutedText}>
                                    {breakdown.attempts} attempt{breakdown.attempts > 1 ? 's' : ''}
                                  </Text>
                                </Flex>
                                <Flex justify="space-between" align="baseline" mb={1}>
                                  <Text fontSize="sm" fontWeight="bold">Avg Score</Text>
                                  <Text fontSize="md" fontWeight="800" color={breakdown.avgPercentage >= 70 ? '#10b981' : '#f59e0b'}>
                                    {breakdown.avgPercentage}%
                                  </Text>
                                </Flex>
                                <Progress
                                  value={breakdown.avgPercentage}
                                  size="xs"
                                  colorScheme={breakdown.avgPercentage >= 70 ? 'green' : 'yellow'}
                                  borderRadius="full"
                                  mb={2.5}
                                />
                                <HStack justify="space-between" fontSize="2xs" color={mutedText} pt={1} borderTop="1px solid" borderColor={borderColor}>
                                  <Text>Total Questions: {breakdown.totalQuestionsAnswered}</Text>
                                  <Text>Pass Rate: <Text as="span" fontWeight="bold" color={breakdown.passRate >= 70 ? '#10b981' : 'red.400'}>{breakdown.passRate}%</Text></Text>
                                </HStack>
                              </Card>
                            ))}
                          </SimpleGrid>
                        </Box>
                      )}

                      {/* Recent Attempts Quick View at Bottom */}
                      <Box>
                        <Flex justify="space-between" align="center" mb={2.5}>
                          <HStack spacing={2} align="center">
                            <FontAwesomeIcon icon={faClock} color={mutedText} />
                            <Heading size="xs" fontWeight="800">
                              Recent Attempts Quick View
                            </Heading>
                          </HStack>
                          {filteredStudentHistory.length > 3 && (
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="cyan"
                              onClick={() => setHistoryActiveTab(1)}
                              rightIcon={<FontAwesomeIcon icon={faChevronRight} />}
                              fontSize="2xs"
                            >
                              View All {filteredStudentHistory.length} Attempts
                            </Button>
                          )}
                        </Flex>

                        <VStack spacing={2} align="stretch">
                          {filteredStudentHistory.slice(0, 3).map((att) => (
                            <Card key={att.attemptId} variant="outline" borderColor={borderColor} borderRadius="lg" p={3} shadow="none" bg={useColorModeValue('white', 'whiteAlpha.50')}>
                              <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
                                <Box>
                                  <HStack spacing={2} align="center">
                                    <Text fontWeight="700" fontSize="xs">
                                      {att.quizTitle || 'Course Quiz'}
                                    </Text>
                                    <Badge colorScheme={att.passed || att.percentage >= 70 ? 'green' : 'red'} borderRadius="md" px={1.5} fontSize="3xs">
                                      {att.passed || att.percentage >= 70 ? 'PASSED' : 'NEEDS PRACTICE'}
                                    </Badge>
                                    <Badge variant="outline" colorScheme="cyan" borderRadius="md" px={1.5} fontSize="3xs">
                                      {att.totalQuestions || 20} Questions
                                    </Badge>
                                  </HStack>
                                  <Text fontSize="2xs" color={mutedText} mt={0.5}>
                                    Chapter: {att.chapterName || 'General Examination'} &bull; ID: {att.attemptId}
                                  </Text>
                                </Box>

                                <HStack spacing={3}>
                                  <Box textAlign="right">
                                    <Text fontSize="xs" fontWeight="800" color={att.percentage >= 70 ? 'emerald.400' : 'red.400'}>
                                      {att.score} / {att.totalQuestions} ({att.percentage}%)
                                    </Text>
                                    <Text fontSize="3xs" color={mutedText}>
                                      {att.timeSpentSeconds ? `${Math.floor(att.timeSpentSeconds / 60)}m ${att.timeSpentSeconds % 60}s` : '2m 0s'}
                                    </Text>
                                  </Box>
                                  <Text fontSize="2xs" color={mutedText} borderLeft="1px solid" borderColor={borderColor} pl={2.5}>
                                    {att.dateString ? new Date(att.dateString).toLocaleString() : new Date(att.timestamp).toLocaleString()}
                                  </Text>
                                </HStack>
                              </Flex>
                            </Card>
                          ))}
                        </VStack>
                      </Box>
                    </VStack>
                  </TabPanel>

                  {/* TAB 2: Full Attempt History Log */}
                  <TabPanel p={0}>
                    <VStack spacing={3} align="stretch">
                      <Flex justify="space-between" align="center">
                        <Text fontSize="xs" color={mutedText}>
                          Showing {filteredStudentHistory.length} quiz attempt{filteredStudentHistory.length > 1 ? 's' : ''}
                          {historyQuestionFilter !== 'all' ? ` (filtered to ${historyQuestionFilter} questions)` : ''}
                        </Text>
                        {historyQuestionFilter !== 'all' && (
                          <Button size="xs" variant="ghost" colorScheme="cyan" onClick={() => setHistoryQuestionFilter('all')} fontSize="2xs">
                            Clear Filter
                          </Button>
                        )}
                      </Flex>

                      {filteredStudentHistory.map((att) => (
                        <Card key={att.attemptId} variant="outline" borderColor={borderColor} borderRadius="xl" p={3.5} shadow="xs" bg={useColorModeValue('white', 'whiteAlpha.50')}>
                          <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
                            <Box>
                              <HStack spacing={2} align="center" flexWrap="wrap">
                                <Text fontWeight="800" fontSize="sm">
                                  {att.quizTitle || 'Course Quiz'}
                                </Text>
                                <Badge colorScheme={att.passed || att.percentage >= 70 ? 'green' : 'red'} borderRadius="md" px={2} fontSize="3xs">
                                  {att.passed || att.percentage >= 70 ? 'PASSED' : 'NEEDS PRACTICE'}
                                </Badge>
                                <Badge variant="subtle" colorScheme="cyan" borderRadius="md" px={2} fontSize="3xs">
                                  {att.totalQuestions || 20} Questions
                                </Badge>
                                <Badge
                                  variant="outline"
                                  colorScheme={att.mode === 'jumbled' || att.mode === 'random' ? 'teal' : 'cyan'}
                                  borderRadius="md"
                                  px={2}
                                  fontSize="3xs"
                                >
                                  {att.mode === 'jumbled' || att.mode === 'random' ? '🎲 Random Order' : '🔢 Sequential'}
                                </Badge>
                              </HStack>
                              <Text fontSize="xs" color={mutedText} mt={0.5}>
                                Chapter: {att.chapterName || 'General Examination'} &bull; Attempt ID: {att.attemptId}
                              </Text>
                            </Box>

                            <HStack spacing={4}>
                              <Box textAlign="right">
                                <Text fontSize="sm" fontWeight="800" color={att.percentage >= 70 ? 'emerald.400' : 'red.400'}>
                                  {att.score} / {att.totalQuestions} ({att.percentage}%)
                                </Text>
                                <Text fontSize="2xs" color={mutedText}>
                                  {att.timeSpentSeconds ? `${Math.floor(att.timeSpentSeconds / 60)}m ${att.timeSpentSeconds % 60}s` : '2m 15s'}
                                </Text>
                              </Box>
                              <Text fontSize="2xs" color={mutedText} borderLeft="1px solid" borderColor={borderColor} pl={3}>
                                {att.dateString ? new Date(att.dateString).toLocaleString() : new Date(att.timestamp).toLocaleString()}
                              </Text>
                              <Button
                                size="xs"
                                variant="outline"
                                colorScheme="cyan"
                                borderRadius="lg"
                                leftIcon={<FontAwesomeIcon icon={expandedAttemptId === att.attemptId ? faChevronUp : faChevronDown} />}
                                onClick={() => setExpandedAttemptId(prev => prev === att.attemptId ? null : att.attemptId)}
                                fontSize="2xs"
                              >
                                {expandedAttemptId === att.attemptId ? 'Hide Questions' : 'Report Card'}
                              </Button>
                            </HStack>
                          </Flex>

                          {/* Expandable Question Responses Report Card */}
                          <Collapse in={expandedAttemptId === att.attemptId} animateOpacity>
                            <Box mt={3} pt={3} borderTop="1px solid" borderColor={borderColor}>
                              <Flex justify="space-between" align="center" mb={2.5}>
                                <Text fontSize="2xs" fontWeight="800" textTransform="uppercase" color={mutedText}>
                                  Detailed Question Report Card {att.questionResponses?.length ? `(${att.questionResponses.length} Questions)` : ''}
                                </Text>
                                <Badge colorScheme="cyan" variant="subtle" fontSize="3xs" px={2} borderRadius="full">
                                  Mode: {att.mode === 'jumbled' || att.mode === 'random' ? 'Random Shuffled' : 'Sequential Order'}
                                </Badge>
                              </Flex>

                              {att.questionResponses && att.questionResponses.length > 0 ? (
                                <VStack spacing={2.5} align="stretch">
                                  {att.questionResponses.map((qr, qIdx) => (
                                    <Box
                                      key={qr.questionId || qIdx}
                                      p={3}
                                      borderRadius="lg"
                                      bg={useColorModeValue('gray.50', 'whiteAlpha.100')}
                                      border="1px solid"
                                      borderColor={qr.isCorrect ? 'rgba(16, 185, 129, 0.35)' : 'rgba(239, 68, 68, 0.35)'}
                                    >
                                      <Flex justify="space-between" align="start" gap={2} mb={1.5}>
                                        <HStack spacing={2} align="start">
                                          <Badge
                                            colorScheme={qr.isCorrect ? 'green' : 'red'}
                                            borderRadius="md"
                                            px={1.5}
                                            py={0.5}
                                            fontSize="3xs"
                                            fontWeight="bold"
                                          >
                                            Q{qIdx + 1}
                                          </Badge>
                                          <Text fontSize="xs" fontWeight="700">
                                            {qr.questionText}
                                          </Text>
                                        </HStack>
                                        <Badge
                                          colorScheme={qr.isCorrect ? 'green' : 'red'}
                                          variant="solid"
                                          borderRadius="md"
                                          px={2}
                                          py={0.5}
                                          fontSize="3xs"
                                        >
                                          <FontAwesomeIcon icon={qr.isCorrect ? faCheck : faTimes} style={{ marginRight: '4px' }} />
                                          {qr.isCorrect ? 'CORRECT' : 'INCORRECT'}
                                        </Badge>
                                      </Flex>

                                      <Flex direction={{ base: 'column', sm: 'row' }} justify="space-between" fontSize="2xs" gap={2} pl={6}>
                                        <Box>
                                          <Text color={mutedText}>Student's Choice:</Text>
                                          <Text fontWeight="600" color={qr.isCorrect ? '#10b981' : 'red.400'}>
                                            {qr.selectedAnswer || '(No answer recorded)'}
                                          </Text>
                                        </Box>
                                        {!qr.isCorrect && qr.correctAnswer && (
                                          <Box>
                                            <Text color={mutedText}>Correct Answer:</Text>
                                            <Text fontWeight="700" color="#10b981">
                                              {qr.correctAnswer}
                                            </Text>
                                          </Box>
                                        )}
                                      </Flex>
                                    </Box>
                                  ))}
                                </VStack>
                              ) : (
                                <Box p={3} bg={useColorModeValue('gray.50', 'whiteAlpha.50')} borderRadius="lg">
                                  <Text fontSize="2xs" color={mutedText} fontStyle="italic">
                                    Individual per-question answers were not recorded on this device for this attempt. (Supported on mobile app v1.0.4+).
                                  </Text>
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </Card>
                      ))}
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
          </ModalBody>

          <ModalFooter borderTop="1px solid" borderColor={borderColor} py={3}>
            <Flex justify="space-between" w="100%" align="center">
              <Text fontSize="2xs" color={mutedText}>
                Mobile users can take 5, 10, or 20 question customized quizzes.
              </Text>
              <Button size="sm" onClick={onHistoryClose} colorScheme="cyan" variant="solid" borderRadius="lg" px={5}>
                Close
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Instructor -> Student Direct Email Notification Modal */}
      <Modal isOpen={isEmailOpen} onClose={onEmailClose} size="xl" isCentered>
        <ModalOverlay backdropFilter="blur(6px)" bg="rgba(0,0,0,0.5)" />
        <ModalContent bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl" shadow="2xl">
          <ModalHeader pb={2}>
            <HStack spacing={3}>
              <Box p={2.5} bg="linear-gradient(135deg, rgba(14, 165, 233, 0.12) 0%, rgba(16, 185, 129, 0.12) 100%)" color="#0284c7" borderRadius="xl">
                <FontAwesomeIcon icon={faPaperPlane} />
              </Box>
              <Box>
                <Heading size="sm" fontWeight="800">
                  Notify Student
                </Heading>
                <Text fontSize="xs" color={mutedText} mt={0.5}>
                  Official Instructor Notification
                </Text>
              </Box>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py={4}>
            <VStack spacing={4} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <FormControl>
                  <FormLabel fontSize="2xs" fontWeight="700" textTransform="uppercase" color={mutedText}>
                    Recipient Student
                  </FormLabel>
                  <Input size="sm" borderRadius="lg" isReadOnly value={`${emailTargetStudent?.userName || 'Student'} (${emailTargetStudent?.userEmail || ''})`} bg={useColorModeValue('gray.50', 'whiteAlpha.50')} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="2xs" fontWeight="700" textTransform="uppercase" color={mutedText}>
                    Course Context
                  </FormLabel>
                  <Input size="sm" borderRadius="lg" isReadOnly value={currentCourseMeta.name} bg={useColorModeValue('gray.50', 'whiteAlpha.50')} />
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <FormControl isRequired>
                  <FormLabel fontSize="2xs" fontWeight="700" textTransform="uppercase" color={mutedText}>
                    Instructor / Sender Name
                  </FormLabel>
                  <Input
                    size="sm"
                    borderRadius="lg"
                    value={instructorSenderName}
                    onChange={(e) => setInstructorSenderName(e.target.value)}
                    placeholder="Instructor Name (e.g. Dr. Cliff Gronseth / Boreddy)"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="2xs" fontWeight="700" textTransform="uppercase" color={mutedText}>
                    Email Subject
                  </FormLabel>
                  <Input
                    size="sm"
                    borderRadius="lg"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Subject line..."
                  />
                </FormControl>
              </SimpleGrid>

              {/* Quick Template Preset Badge Buttons */}
              <Box>
                <Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={mutedText} mb={2}>
                  Quick Message Presets
                </Text>
                <HStack spacing={2} flexWrap="wrap">
                  <Button
                    size="xs"
                    variant="outline"
                    colorScheme="green"
                    borderRadius="lg"
                    leftIcon={<FontAwesomeIcon icon={faTrophy} />}
                    onClick={() => {
                      setEmailSubject(`Praise: Outstanding Performance in ${currentCourseMeta.name}`);
                      setEmailBody(`Hi ${emailTargetStudent?.userName || 'Student'},\n\nCongratulations on your impressive quiz results in ${currentCourseMeta.name}! Your dedication to mastering the clinical material is truly inspiring. Keep up the wonderful effort!`);
                    }}
                  >
                    Praise High Score
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    colorScheme="teal"
                    borderRadius="lg"
                    leftIcon={<FontAwesomeIcon icon={faArrowTrendUp} />}
                    onClick={() => {
                      setEmailSubject(`Next Steps & Study Tips for ${currentCourseMeta.name}`);
                      setEmailBody(`Hi ${emailTargetStudent?.userName || 'Student'},\n\nWe wanted to encourage you to take the next chapter assessment in ${currentCourseMeta.name}. Feel free to review the clinical media modules before your next attempt.`);
                    }}
                  >
                    Encourage Next Quiz
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    colorScheme="cyan"
                    borderRadius="lg"
                    leftIcon={<FontAwesomeIcon icon={faClock} />}
                    onClick={() => {
                      setEmailSubject(`Instructor Office Hours & Support: ${currentCourseMeta.name}`);
                      setEmailBody(`Hi ${emailTargetStudent?.userName || 'Student'},\n\nIf you have any questions regarding tricky clinical concepts or anatomy modules in ${currentCourseMeta.name}, please feel free to reach out to us directly or attend upcoming office hours.`);
                    }}
                  >
                    Office Hours & Support
                  </Button>
                </HStack>
              </Box>

              <FormControl isRequired>
                <FormLabel fontSize="2xs" fontWeight="700" textTransform="uppercase" color={mutedText}>
                  Message Content
                </FormLabel>
                <Textarea
                  size="sm"
                  borderRadius="lg"
                  rows={5}
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  placeholder="Write your custom email message here..."
                />
              </FormControl>

              <Box p={2.5} bg={useColorModeValue('gray.50', 'whiteAlpha.50')} borderRadius="lg" border="1px dashed" borderColor={borderColor}>
                <Text fontSize="3xs" color={mutedText}>
                  Signature automatically attached: <Text as="span" fontWeight="bold">Best regards, {instructorSenderName} &bull; {currentCourseMeta.org || userOrg || 'XNAV Studio'} Instructor Team</Text>
                </Text>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter borderTop="1px solid" borderColor={borderColor} gap={3}>
            <Button size="sm" variant="ghost" onClick={onEmailClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              colorScheme="green"
              leftIcon={<FontAwesomeIcon icon={faPaperPlane} />}
              isLoading={sendingEmail}
              loadingText="Sending via SES..."
              onClick={handleSendEmailToStudent}
            >
              Send Notification
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Executive PDF Report Preview Modal */}
      <Modal isOpen={isPdfPreviewOpen} onClose={onPdfPreviewClose} size="4xl" isCentered scrollBehavior="inside">
        <ModalOverlay backdropFilter="blur(6px)" bg="rgba(0,0,0,0.5)" />
        <ModalContent bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl" shadow="2xl">
          <ModalHeader pb={2}>
            <Flex justify="space-between" align="center" pr={6}>
              <HStack spacing={3}>
                <Box p={2.5} bg="rgba(239, 68, 68, 0.1)" color="#ef4444" borderRadius="xl">
                  <FontAwesomeIcon icon={faFilePdf} />
                </Box>
                <Box>
                  <Heading size="sm" fontWeight="800">
                    Executive PDF Report Preview
                  </Heading>
                  <Text fontSize="xs" color={mutedText} mt={0.5}>
                    Document: {currentCourseMeta.name} &bull; Ready for Export & Print
                  </Text>
                </Box>
              </HStack>

              <HStack spacing={2}>
                <Button
                  size="xs"
                  variant={pdfWithGraphs ? 'solid' : 'outline'}
                  colorScheme="green"
                  onClick={() => setPdfWithGraphs(true)}
                >
                  With Graphs
                </Button>
                <Button
                  size="xs"
                  variant={!pdfWithGraphs ? 'solid' : 'outline'}
                  colorScheme="cyan"
                  onClick={() => setPdfWithGraphs(false)}
                >
                  Roster Only
                </Button>
              </HStack>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py={4}>
            {/* Formatted Document Canvas Simulator */}
            <Box
              p={6}
              bg="white"
              color="#0f172a"
              borderRadius="xl"
              border="1px solid #e2e8f0"
              shadow="md"
              fontFamily="sans-serif"
            >
              {/* Document Banner */}
              <Flex bg="#0B192C" p={5} borderRadius="lg" justify="space-between" align="center" color="white" mb={5}>
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="#10B981" letterSpacing="1px" textTransform="uppercase">
                    XN&#923;V Learning Ecosystem
                  </Text>
                  <Heading size="md" color="white" mt={0.5}>
                    {currentCourseMeta.name} Analytics Report
                  </Heading>
                  <Text fontSize="2xs" color="gray.400" mt={1}>
                    Organization: {currentCourseMeta.org || userOrg} &bull; Generated: {new Date().toLocaleDateString()}
                  </Text>
                </Box>
                <Badge bg="linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)" color="white" fontSize="2xs" px={2.5} py={1} borderRadius="md">
                  OFFICIAL REPORT
                </Badge>
              </Flex>

              {/* KPI Summary (if graphs enabled) */}
              {pdfWithGraphs && summary && (
                <Box mb={6}>
                  <SimpleGrid columns={4} spacing={3} mb={5}>
                    <Box p={3} bg="#f8fafc" borderRadius="md" border="1px solid #e2e8f0" textAlign="center">
                      <Text fontSize="3xs" color="#64748b" fontWeight="bold" textTransform="uppercase">Total Enrolled</Text>
                      <Text fontSize="md" fontWeight="bold" color="#0f172a">{summary.totalEnrolled || students.length}</Text>
                    </Box>
                    <Box p={3} bg="#f8fafc" borderRadius="md" border="1px solid #e2e8f0" textAlign="center">
                      <Text fontSize="3xs" color="#64748b" fontWeight="bold" textTransform="uppercase">Active Students</Text>
                      <Text fontSize="md" fontWeight="bold" color="#0f172a">{summary.activeStudents || 0}</Text>
                    </Box>
                    <Box p={3} bg="#f8fafc" borderRadius="md" border="1px solid #e2e8f0" textAlign="center">
                      <Text fontSize="3xs" color="#64748b" fontWeight="bold" textTransform="uppercase">Class Average</Text>
                      <Text fontSize="md" fontWeight="bold" color="#10b981">{summary.averageScore || 0}%</Text>
                    </Box>
                    <Box p={3} bg="#f8fafc" borderRadius="md" border="1px solid #e2e8f0" textAlign="center">
                      <Text fontSize="3xs" color="#64748b" fontWeight="bold" textTransform="uppercase">Pass Rate</Text>
                      <Text fontSize="md" fontWeight="bold" color="#06b6d4">{summary.overallPassRate || 0}%</Text>
                    </Box>
                  </SimpleGrid>

                  {/* 4 Visual Charts 2x2 Grid Simulator */}
                  <SimpleGrid columns={2} spacing={4} mb={5}>
                    {/* Graph 1: Mastery Breakdown */}
                    <Box p={3.5} bg="#f8fafc" borderRadius="lg" border="1px solid #e2e8f0">
                      <Text fontSize="2xs" fontWeight="bold" color="#0f172a" mb={2}>
                        Mastery Breakdown & Score Distribution
                      </Text>
                      <Flex align="center" justify="space-around" gap={2}>
                        <DonutChart data={summary.masteryBreakdown || []} total={summary.totalAttempts || 0} size={110} />
                        <VStack align="stretch" spacing={1} flex={1}>
                          {summary.masteryBreakdown?.map((m, idx) => (
                            <Flex key={idx} justify="space-between" fontSize="3xs">
                              <Text color="#64748b" fontWeight="600">{m.label.split(' ')[0]}</Text>
                              <Text fontWeight="bold" color={m.color}>{m.count} ({m.percentage}%)</Text>
                            </Flex>
                          ))}
                        </VStack>
                      </Flex>
                    </Box>

                    {/* Graph 2: Monthly Intake & Growth 2D */}
                    <Box p={3.5} bg="#f8fafc" borderRadius="lg" border="1px solid #e2e8f0">
                      <Text fontSize="2xs" fontWeight="bold" color="#0f172a" mb={1}>
                        Monthly Student Intake & Growth
                      </Text>
                      <MonthlyIntakeChart data={summary.monthlyIntake || []} />
                    </Box>

                    {/* Graph 3: Quiz & Chapter Pass Rates */}
                    <Box p={3.5} bg="#f8fafc" borderRadius="lg" border="1px solid #e2e8f0">
                      <Text fontSize="2xs" fontWeight="bold" color="#0f172a" mb={2}>
                        Quiz & Chapter Pass Rates
                      </Text>
                      <VStack spacing={1.5} align="stretch">
                        {(summary.quizBreakdown || []).slice(0, 3).map((q) => (
                          <Box key={q.quizId}>
                            <Flex justify="space-between" fontSize="3xs" mb={0.5}>
                              <Text fontWeight="600" isTruncated maxW="150px" color="#334155">{q.title}</Text>
                              <Text fontWeight="bold" color={q.passRate >= 70 ? '#10b981' : '#f59e0b'}>{q.passRate}%</Text>
                            </Flex>
                            <Progress value={q.passRate} size="2xs" borderRadius="full" colorScheme="green" />
                          </Box>
                        ))}
                      </VStack>
                    </Box>

                    {/* Graph 4: Submissions Activity Timeline */}
                    <Box p={3.5} bg="#f8fafc" borderRadius="lg" border="1px solid #e2e8f0">
                      <Text fontSize="2xs" fontWeight="bold" color="#0f172a" mb={1}>
                        Quiz Submissions Trend Curve
                      </Text>
                      <LinearTrendChart data={summary.activityTimeline || []} />
                    </Box>
                  </SimpleGrid>
                </Box>
              )}

              {/* Roster Table Preview */}
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="#0f172a" mb={2}>
                  Enrolled Students Performance Roster ({filteredStudents.length} Students)
                </Text>
                <Table size="xs" variant="simple">
                  <Thead bg="#0B192C">
                    <Tr>
                      <Th color="white" fontSize="3xs">Student</Th>
                      <Th color="white" fontSize="3xs">Email</Th>
                      <Th color="white" fontSize="3xs">Status</Th>
                      <Th color="white" fontSize="3xs">Attempts</Th>
                      <Th color="white" fontSize="3xs">Avg %</Th>
                      <Th color="white" fontSize="3xs">Pass %</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredStudents.slice(0, 10).map((s) => (
                      <Tr key={s.userId} _hover={{ bg: '#f1f5f9' }}>
                        <Td fontSize="2xs" fontWeight="600">{s.userName}</Td>
                        <Td fontSize="2xs" color="#64748b">{s.userEmail}</Td>
                        <Td fontSize="2xs">{s.status}</Td>
                        <Td fontSize="2xs" fontWeight="bold">{s.quizAttemptsCount || 0}</Td>
                        <Td fontSize="2xs" fontWeight="bold" color="#10b981">{s.avgScorePercentage || 0}%</Td>
                        <Td fontSize="2xs">{s.passRate || 0}%</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                {filteredStudents.length > 10 && (
                  <Text fontSize="3xs" color="#64748b" textAlign="center" mt={2} fontStyle="italic">
                    + {filteredStudents.length - 10} more students included in the complete downloaded PDF document.
                  </Text>
                )}
              </Box>
            </Box>
          </ModalBody>

          <ModalFooter borderTop="1px solid" borderColor={borderColor} gap={3}>
            <Button size="sm" variant="ghost" onClick={onPdfPreviewClose}>
              Close
            </Button>
            <Button
              size="sm"
              colorScheme="red"
              leftIcon={<FontAwesomeIcon icon={faDownload} />}
              onClick={() => {
                handleDownloadPDF(pdfWithGraphs);
                onPdfPreviewClose();
              }}
            >
              Download PDF Report
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
